/* eslint-disable @typescript-eslint/ban-ts-comment -- locale data file: @ts-nocheck lets the parameterized i18n functions stay un-annotated */
// @ts-nocheck
/**
 * English is the structural source of truth for translations:
 * `Translations = typeof en` (declared at the bottom of this file). de.ts and
 * vi.ts are typed `: Translations` and validated against this shape, so any
 * key drift in them surfaces as a type error.
 *
 * @ts-nocheck keeps this file from erroring on the parameterized translation
 * functions, whose params are intentionally left un-annotated - and avoids a
 * circular self-reference (`en` being typed by `typeof en`).
 */
export const en = {
  back: 'Back',
  save: 'Save changes',
  saveShort: 'Save',
  createShort: 'Create',
  saving: 'Saving…',
  loading: 'Loading…',
  cancel: 'Cancel',
  ok: 'OK',
  confirm: 'Confirm',
  close: 'Close',
  edit: 'Edit',
  delete: 'Delete',
  no: 'No',
  error: 'Something went wrong',
  saved: 'Saved',
  prevMonth: 'Previous month',
  nextMonth: 'Next month',
  pickDate: 'Pick a date',

  signOut: 'Sign Out',
  signOutConfirm: 'Are you sure you want to sign out?',
  versionCopied: 'Version copied',
  versionCopyHint: 'Tap to copy version',
  companyCredit:
    'Software and copyright owned by Ninety Six Digital. To learn more about our advertising, business management software, and AI solutions, please contact us via',
  companyCreditAnd: 'and',

  unsavedChangesTitle: 'Discard changes?',
  unsavedChangesText: 'You have unsaved changes. Do you want to discard them?',
  discardChanges: 'Discard',

  payments: {
    title: 'Payments',
    onlineTitle: 'Online payment providers',
    intro:
      'Ready-made providers (PayPal, Menucat). Connect as many as you like - each one you switch on appears as its own choice at checkout, and the payment is captured automatically and settles to your own account.',
    paymentWindowTitle: 'Payment window',
    paymentWindowFooter:
      'How long an unpaid order stays open for the customer to transfer and upload their receipt. After this it is cancelled automatically and no receipt can be uploaded. Set per order type.',
    paymentWindowDialogTitle: 'Minutes to pay (1–1440)',
    paymentWindowPrompt: (label: string) =>
      `How many minutes should a ${label} order wait for payment before it is cancelled?`,
    paymentWindowValue: (mins: number) => `Cancels ${mins} min after ordering`,
    paymentWindowSet: (label: string, mins: number) =>
      `${label}: unpaid orders cancel after ${mins} min`,
    paymentWindowInvalid: 'Enter a whole number of minutes between 1 and 1440.',
    manualTitle: 'Payment methods you add',
    manualIntro:
      'Bank transfer, VietQR or e-wallet that you set up yourself - you enter the account details and QR, the customer pays offline and uploads a receipt. Unlike the online providers above, these are not captured automatically.',
    none: 'No payment provider connected yet.',
    connect: '+ Connect a provider',
    update: 'Update',
    live: 'Live',
    testMode: 'Test mode',
    providerPaypal: 'PayPal Business',
    providerPaycat: 'Menucat (card payments)',
    activatedToast: 'Now used for online checkout',
    deactivatedToast: 'Online checkout turned off',
    connectTitle: 'Connect a provider',
    updateTitle: 'Update connection',
    providerLabel: 'Provider',
    paypalChoice: 'PayPal Business',
    paycatChoice: 'Menucat',
    paypalCredsTitle: 'PayPal API credentials',
    clientId: 'Client ID',
    clientIdPlaceholder: 'From your PayPal Business app',
    secret: 'Secret',
    secretPlaceholder: 'Paste the app secret',
    webhookId: 'Webhook ID (optional)',
    webhookIdPlaceholder: 'Enables instant payment confirmation',
    paypalFooter:
      'Create a REST app in your PayPal Business account (Developer Dashboard) and paste its Client ID and Secret here. Payments will settle directly to your PayPal account.',
    webhookFooter:
      'On connect, a webhook is registered with PayPal automatically so paid orders confirm instantly - no setup needed. If it can’t be registered, orders still confirm, just a little slower (checked every minute).',
    paycatCredsTitle: 'Paycat credentials',
    serviceUrl: 'Service URL',
    tenantId: 'Tenant ID',
    tenantSecret: 'Tenant secret',
    settingsTitle: 'Settings',
    useForCheckout: 'Use for online checkout',
    useForCheckoutHelp:
      'Offer this provider at checkout. Providers run side by side - turning this on leaves the others on, and the customer picks. Only the Test and Live keys of THIS provider are exclusive.',
    settingsFooter:
      'Turning Use for online checkout on offers this provider - in the mode selected above - alongside any others you already run. Enter your live keys with it on to switch this provider to live in one step.',
    errNoWhitespace: "Remove the spaces — API keys don't contain any.",
    errClientIdEmail: 'This looks like your PayPal login email, not the REST app Client ID.',
    errInvalidUrl: 'Enter a valid https:// address.',
    errCheck: 'Check this.',
    connectFailed: "Couldn't connect the payment provider. Please try again.",
    connectedToast: 'Payment provider connected',
    unsavedTitle: 'Unsaved details',
    unsavedText: (name) => `You've entered details for ${name}. Save them, or discard and switch?`,
    save: 'Save',
    discardSwitch: 'Discard & switch',
    cancel: 'Cancel',
    disconnect: 'Disconnect',
    disconnectConfirm:
      "Disconnect this provider? Its saved credentials are removed - you'll need to re-enter them to reconnect.",
    disconnectedToast: 'Provider disconnected',
    notConnected: 'Not connected',
    connectShort: 'Connect',
    overwriteNote:
      'This provider is already connected. Saving overwrites the saved connection - there is only one per provider.',
    // Dual test/live credentials + the safety banner (a shop can hold both key
    // sets per provider and toggle which mode the storefront transacts in).
    modeTest: 'Sandbox',
    modeLive: 'Live',
    stateConnected: 'Connected',
    stateActive: 'Active - customers pay here',
    sandboxBannerTitle: 'Sandbox mode is active',
    sandboxBannerBody:
      'Your storefront is in SANDBOX mode - customers are NOT charged real money. Switch the active provider to Live to take real payments.',
    modeSectionTitle: 'Sandbox or live keys',
    modeSectionFooter:
      "Choose which key set you are entering. Sandbox uses the provider's test environment and never charges real money; Live takes real payments. You can save both and switch anytime.",
    modeHasCreds: 'Keys are already saved for this mode - saving overwrites them.',
    modeNoCreds: 'No keys saved for this mode yet.',
    downgradeTitle: 'Switch to sandbox?',
    downgradeText:
      'Your storefront will STOP taking real money and switch to SANDBOX (test) mode. Continue?',
    // ── Checkout badges: an owner-written pill beside ONE payment method ──
    badgesTitle: 'Checkout badges',
    badgesFooter:
      'A short note shown beside a payment method at checkout, e.g. "Recommended". Customers read it exactly as you type it, so write it in their language.',
    badgeDialogTitle: 'Checkout badge',
    badgePrompt: (method: string) =>
      `Text to show beside ${method} at checkout. Leave it empty to remove the badge.`,
    badgeNone: 'No badge',
    badgeTooLong: (max: number) => `Keep the badge to ${max} characters or fewer.`,
    badgeSetToast: (method: string) => `Badge added to ${method}`,
    badgeClearedToast: (method: string) => `Badge removed from ${method}`,
    badgeMethodCash: 'Cash',
    badgeMethodCardOnHandoff: 'Card at pickup / delivery',
  },

  nav: {
    operations: 'Operations',
    functionality: 'Functionality',
    reservations: 'Reservations',
    orders: 'Orders',
    tables: 'Tables',
    customers: 'Customer',
    menu: 'Menu',
    discount: 'Discount',
    reports: 'Reports',
    customerService: 'Customer Service',
    settings: 'Settings',
    actionCenter: 'Action Center',
    pendingQueue: 'Pending Queue',
    aiAssistant: 'AI Assistant',
    inbox: 'Inbox',
    collapse: 'Collapse',
    expand: 'Expand',
  },

  views: {
    list: 'List',
    timeline: 'Timeline',
    week: 'Week',
    plan: 'Plan',
  },

  settings: {
    title: 'Settings',
    searchSettings: 'Search settings',
    dashboard: 'Dashboard',
    account: 'Account',
    email: 'Email',
    restaurant: 'Restaurant',
    reservations: 'Reservations',
    capacity: 'Capacity',
    tables: 'Tables',
    tablePlan: 'Table Plan',
    menu: 'Menu',
    menus: 'Menus, Categories & Dishes',
    myRestaurant: 'My Restaurant',
    storefront: 'Storefront',
    notifications: 'Notifications',
    pushNotifications: 'Push Notifications',
    restaurantSetup: 'Business Profile',
    switchRestaurant: 'Switch Restaurant',
    searchRestaurants: 'Search restaurants',
    noRestaurantsMatch: 'No restaurants match',
    switchRestaurantConfirm: 'Switch to a different restaurant?',
    language: 'Language',
    mail: {
      title: 'Notifications',
      reservationTitle: 'Reservations',
      reservationCustomer: 'Email guests about their booking',
      reservationOwner: 'App notification to owner & staff of new bookings',
      reservationOwnerEmail: 'Email to owner & staff of new bookings',
      reservationReminder: 'Email guests a booking reminder',
      // Each order row names its RECIPIENT. The customer block and the
      // owner-email block both list pickup + delivery, so a bare "Pickup orders"
      // appeared twice on the page and only the group heading told them apart -
      // which scrolls out of view on a phone. Matches the self-describing
      // reservation rows above.
      customerPickupTitle: 'Email customer about pickup orders',
      customerDeliveryTitle: 'Email customer about delivery orders',
      ownerPickupTitle: 'Email owner & staff about pickup orders',
      ownerDeliveryTitle: 'Email owner & staff about delivery orders',
      // NotificationSettingsPage reuses this block for the APP-ALERT axis
      // (ownerNotifyEnabled). Separate keys so an email row and a push row are
      // never worded the same.
      pushPickupTitle: 'App notification about pickup orders',
      pushDeliveryTitle: 'App notification about delivery orders',
      customerGroup: 'Emails to customer',
      ownerGroup: 'App notification to you & staff',
      byOrderType: 'by order type',
      byPayment: 'by payment method',
      ownerEmailGroup: 'Email to owner & staff',
      allMaster: 'All payment methods',
      cash: 'Cash',
      manual: 'Bank transfer',
      online: 'Card / PayPal',
      customerNote:
        'Off = the guest gets no email for that payment method. In-app order status still updates.',
      ownerNote: 'Off = no push or inbox alert to you and your staff for new orders paid that way.',
    },
  },

  notifications: {
    title: 'Push Notifications',
    statusTitle: 'Notification status',
    deviceIphone: 'This iPhone or iPad',
    deviceAndroid: 'This Android device',
    deviceBrowser: 'This browser',
    statusOn: 'On',
    statusOnDetail: "You'll receive alerts on this device in real time.",
    statusOff: 'Off',
    statusOffDetail: "Alerts aren't enabled yet. Turn them on to be notified in real time.",
    statusBlocked: 'Blocked',
    statusBlockedNative:
      'Notifications are blocked. Turn them on for this app in your device Settings.',
    statusBlockedWeb:
      "Notifications are blocked. Turn them on in your browser's site settings, then reload this page.",
    statusBrowserBlocked: 'Blocked by browser',
    statusBrowserBlockedDetail:
      'This browser blocks push notifications. In Brave, open Settings → Privacy and security and turn on “Use Google services for push messaging”, then restart Brave. Or use Chrome or the phone app.',
    statusUnsupported: 'Not supported',
    statusUnsupportedDetail: "This browser can't receive push notifications.",
    statusUnconfigured: 'Unavailable',
    statusUnconfiguredDetail: 'Push notifications are not set up in this build.',
    statusChecking: 'Checking…',
    statusCheckingDetail: 'Checking notification permission…',
    enableButton: 'Enable notifications',
    reservationSection: 'Reservations',
    newBooking: 'New booking',
    cancellation: 'Guest cancellation',
    noShow: 'No-show alert',
    orderSection: 'Online orders',
    prepSection: 'Kitchen timing',
    prepReminder: 'Start-prep reminder',
    prepReminderHint:
      'Alerts your team when it is time to start cooking a scheduled order, counted back from the handoff time.',
    hint: 'Choose the events you want to be alerted about in real time. Alerts arrive only while the status above shows "On".',
    testSection: 'Test notification',
    testDelayTitle: 'Send after',
    testDelaySeconds: (n) => `${n} seconds`,
    testButton: 'Send a test notification',
    testHint:
      'Tap the button, then leave the app - the test alert arrives on this device after the delay you picked.',
    testPushTitle: 'Test notification',
    testPushBody: 'Push notifications are working on this device.',
    testScheduled: (n) => `Test alert on its way - arrives in ${n} seconds.`,
    testNoDevice: 'No device is registered yet. Turn notifications on first.',
  },

  reservations: {
    // Reservation-availability strip above the workspace (ReservationPauseCard).
    // Non-paused state reuses openingHours.status* labels; these cover the
    // paused state, the cell label, and the pause action-sheet / toasts.
    pause: {
      cellLabel: 'Reservations',
      open: 'Open',
      off: 'Off',
      pausedUntilClose: 'Paused · close',
      pausedUntilTime: (time) => `Paused · ${time}`,
      action: {
        heading: 'Reservations',
        pause30: 'Pause 30 minutes',
        pause1hour: 'Pause 1 hour',
        pauseHours: (n) => `Pause ${n} hours`,
        pauseUntilClose: 'Pause until close',
        resumeNow: 'Resume now',
        cancel: 'Cancel',
      },
      toast: {
        pausedFor: (minutes) => `Reservations paused for ${minutes} min`,
        pausedUntilClose: 'Reservations paused until close',
        resumed: 'Reservations resumed',
      },
    },
    title: 'Reservations',
    pending: 'Pending',
    upcoming: 'Upcoming',
    today: 'Today',
    newReservation: 'New Reservation',
    detailTitle: 'Reservation',
    guest: 'Guest',
    guests: 'Guests',
    partyOf: 'Party of',
    notes: 'Notes',
    all: 'All',
    filterAria: 'Filter',
    status: {
      PENDING: 'Pending',
      CONFIRMED: 'Confirmed',
      CANCELLED: 'Cancelled',
      REJECTED: 'Rejected',
      NO_SHOW: 'No show',
      ARRIVED: 'Arrived',
      COMPLETED: 'Completed',
    },
    actions: {
      confirm: 'Confirm',
      reject: 'Reject',
      cancel: 'Cancel',
      noShow: 'No show',
      arrived: 'Mark arrived',
    },
    count: (n) => `${n} reservation${n !== 1 ? 's' : ''}`,
    // Plain-text summary strip above the day's list. Leads with covers (the real
    // load), plus the two facts the status sections below don't show: the peak
    // hour and the load arriving in the next 2h (today only).
    covers: (bookings, guests) =>
      `${bookings} booking${bookings !== 1 ? 's' : ''} · ${guests} guest${guests !== 1 ? 's' : ''}`,
    peak: (time, guests) => `Peak ${time} · ${guests} guest${guests !== 1 ? 's' : ''}`,
    nextWindow: (guests) => `Next 2h: ${guests} guest${guests !== 1 ? 's' : ''}`,
    guestsCount: (guests) => `${guests} guest${guests !== 1 ? 's' : ''}`,
    guestsByHour: 'Guests by hour',
    nowLabel: 'Now',
    slotCap: (n) => `max ${n}/slot`,
    advCap: (pacing, concurrent) => `${pacing}/15' · ${concurrent} concurrent`,
    defaultShift: 'Default',
    // Badge labels keyed by DisplayStatus (see utils/status.ts). A future
    // confirmed-but-not-arrived booking is shown as `confirmed` instead of
    // `waiting`; that branch lives in getDisplayStatusLabel.
    displayStatus: {
      pending: 'Pending',
      confirmed: 'Confirmed',
      rejected: 'Rejected',
      cancelled: 'Cancelled',
      rescheduled: 'Rescheduled',
      waiting: 'Waiting',
      arrived: 'Arrived',
      seated: 'Seated',
      'in-progress': 'In progress',
      completed: 'Completed',
      'no-show': 'No Show',
    },
    occasion: {
      BIRTHDAY: '🎂 Birthday',
      ANNIVERSARY: '💛 Anniversary',
      DATE: '🌹 Date night',
      BUSINESS: '💼 Business',
    },
    seating: {
      WINDOW: 'Window',
      OUTDOOR: 'Outdoor',
      QUIET: 'Quiet corner',
      BOOTH: 'Booth',
      BABY_CHAIR: 'Baby chair',
      HIGH_CHAIR: 'High chair',
      WHEELCHAIR_ACCESS: 'Wheelchair access',
      GROUND_FLOOR: 'Ground floor',
      NO_PREFERENCE: 'No preference',
    },
    swipe: { accept: 'Accept', decline: 'Decline' },
    toast: {
      confirmed: 'Reservation confirmed',
      rejected: 'Reservation rejected',
      cancelled: 'Reservation cancelled',
      arrived: 'Marked as arrived',
      seated: 'Marked as seated',
      inProgress: 'Marked as in progress',
      completed: 'Marked as completed',
      noShow: 'Marked as no show',
    },
    errors: {
      confirm: 'Failed to confirm reservation.',
      reject: 'Failed to reject reservation.',
      cancel: 'Failed to cancel reservation.',
    },
    prompts: {
      rejectReason: 'Reason for rejection:',
      rejectTitle: 'Reject Reservation',
      cancelConfirm: 'Cancel this reservation?',
      cancelTitle: 'Cancel Reservation',
    },
    empty: {
      errorTitle: 'Error loading reservations',
      errorDesc: 'Please try again.',
      noneTitle: 'No reservations',
      noneDesc: 'No reservations for this date.',
      timelineTitle: 'Nothing to show yet',
      timelineDesc: 'Add tables in Capacity setup and bookings will appear here as bars.',
      planTitle: 'No tables yet',
      planDesc: 'Add tables in Settings → Reservation Setup to see the floor plan.',
    },
    pendingQueue: {
      title: (n) => `Pending (${n})`,
      emptyTitle: 'All caught up!',
      emptyDesc: 'No pending reservations to review.',
      filterByDate: 'Filter by date',
      showAll: 'Show all',
      emptyDateTitle: 'None on this date',
      emptyDateDesc: 'No pending reservations for the selected date.',
    },
    source: {
      ONLINE: 'Online',
      PHONE: 'Phone',
      WALK_IN: 'Walk-in',
      THIRD_PARTY: 'Third-party',
    },
    plan: {
      noZone: 'No zone',
      seatCount: (seats) => `${seats} ${seats === 1 ? 'seat' : 'seats'}`,
      tableAria: (name, seats) => `Table ${name}, ${seats} seats`,
    },
    timeline: {
      unzoned: 'Unzoned',
      unassignedCount: (n) => `Unassigned · ${n}`,
    },
    week: {
      // Per-day header: live bookings · total covers (e.g. "5 · 18p").
      summary: (count, covers) => `${count} · ${covers}p`,
      none: 'No bookings',
    },
    detail: {
      labels: 'Labels',
      none: 'No reservation selected',
      noneDesc: 'Pick a reservation from the list to see its details here.',
      notFound: 'Reservation not found',
      minutesShort: 'min',
      conflict: (n) =>
        n === 1 ? '1 other booking in this time slot' : `${n} other bookings in this time slot`,
      call: 'Call',
      message: 'Message',
      phoneCopied: 'Phone number copied',
      status: 'Status',
      specialRequests: 'Special Requests',
      occasion: 'Occasion',
      seating: 'Seating & access',
      guestInfo: 'Guest Information',
      phone: 'Phone',
      email: 'Email',
      source: 'Source',
      internalNote: 'Internal Note',
      cancellationReason: 'Cancellation Reason',
      timeline: 'Timeline',
      events: {
        created: 'Created',
        confirmed: 'Confirmed',
        arrived: 'Arrived',
        completed: 'Completed',
        cancelled: 'Cancelled',
        noShow: 'No Show',
      },
      aria: {
        close: 'Close',
        call: 'Call guest',
        message: 'Message guest',
        edit: 'Edit booking',
      },
    },
    menu: {
      confirm: 'Confirm Reservation',
      decline: 'Decline Reservation',
      cancel: 'Cancel Reservation',
      reactivate: 'Reactivate Reservation',
      markArrived: 'Mark as Arrived',
      markSeated: 'Mark as Seated',
      markNoShow: 'Mark as No Show',
      markCompleted: 'Mark as Completed',
      markNotStarted: 'Not started',
      markInProgress: 'In progress',
      markRescheduled: 'Rescheduled',
    },
  },

  // State-change audit history surfaced in the order / reservation inspectors.
  // Owner-facing, non-technical wording. `actor` covers both the order
  // CancelActor (OWNER/STAFF/CUSTOMER/SYSTEM) and the reservation
  // ReservationActorType (GUEST/STAFF/SYSTEM).
  audit: {
    orderKind: {
      CREATED: 'Placed',
      CONFIRMED: 'Confirmed',
      COMPLETED: 'Completed',
      CANCELLED: 'Cancelled',
      PROOF_UPLOADED: 'Payment proof uploaded',
      PROOF_ACCEPTED: 'Payment proof accepted',
      PROOF_REJECTED: 'Payment proof rejected',
      PAYMENT_COLLECTED: 'Payment collected',
      PAYMENT_VOIDED: 'Payment voided',
      REFUND_ISSUED: 'Refund issued',
      PREP_REMINDER_SENT: 'Prep reminder sent',
      PREP_ADVANCED: 'Prep started',
      PREP_RECALLED: 'Prep recalled',
    },
    reservationEvent: {
      CREATED: 'Created',
      CONFIRMED: 'Confirmed',
      REJECTED: 'Rejected',
      CANCELLED_BY_GUEST: 'Cancelled by guest',
      CANCELLED_BY_STAFF: 'Cancelled by staff',
      MODIFIED: 'Modified',
      REACTIVATED: 'Reactivated',
      AUTO_ARRIVED: 'Auto-arrived',
      AUTO_COMPLETED: 'Auto-completed',
      NO_SHOW_ALERTED: 'No-show alerted',
      ARRIVAL_MARKED: 'Arrival updated',
      STATUS_CHANGED: 'Status changed',
    },
    actor: {
      OWNER: 'Owner',
      STAFF: 'Staff',
      CUSTOMER: 'Customer',
      SYSTEM: 'System',
      GUEST: 'Guest',
    },
  },

  restaurantSetup: {
    title: 'Restaurant Setup',
    basicInfo: 'Basic Info',
    hours: 'Hours',
    basicInfoSection: 'Basic information',
    venueLanguageSection: 'Language',
    venueLanguage: 'Venue language',
    venueLanguageFooter:
      'The language used on printed tickets and as the default across the dashboard and online store.',
    name: 'Restaurant name',
    namePlaceholder: 'My Restaurant',
    description: 'Description',
    descriptionPlaceholder: 'A short description shown to customers',
    address: 'Street and number',
    addressPlaceholder: '123 Main St',
    postalCode: 'Postal code',
    postalCodePlaceholder: '10115',
    city: 'City',
    cityPlaceholder: 'Berlin',
    country: 'Country',
    contactEmail: 'Contact email',
    contactEmailPlaceholder: 'hello@restaurant.com',
    contactPhone: 'Contact phone',
    contactPhonePlaceholder: '+1 555 000 0000',
    contactPhoneInvalid: 'Enter a valid phone number like +49 30 1234567.',
    socialSection: 'Social media & website',
    facebookUrl: 'Facebook',
    facebookUrlPlaceholder: '@myrestaurant',
    instagramUrl: 'Instagram',
    instagramUrlPlaceholder: '@myrestaurant',
    tiktokUrl: 'TikTok',
    tiktokUrlPlaceholder: '@myrestaurant',
    websiteUrl: 'Website',
    websiteUrlPlaceholder: 'myrestaurant.com',
    socialFooter:
      'Shown to guests on your online store. You can paste a full link or just your handle. Leave a field empty to remove it.',
    openingHours: 'Opening hours',
    closed: 'Closed',
    openingHoursNote:
      'To edit opening hours, go to the Opening Hours settings in the backend or contact support.',
    saveFailed: 'Save failed',
  },

  editReservation: {
    title: 'Edit Reservation',
    date: 'Date',
    time: 'Time',
    partySize: 'Party Size',
    duration: 'Duration (min)',
    guestName: 'Guest Name',
    guestEmail: 'Guest Email',
    guestPhone: 'Guest Phone',
    internalNote: 'Internal Note',
    save: 'Save',
    sectionWhen: 'When & party',
    sectionGuest: 'Guest',
    sectionOccasion: 'Occasion',
    sectionSeating: 'Seating & access',
    sectionInternal: 'Internal',
    guestNamePlaceholder: 'Guest name',
    notePlaceholder: 'Staff notes…',
    updated: 'Reservation updated',
    updateFailed: 'Failed to update reservation.',
  },

  createReservation: {
    fullName: 'Full name',
    namePlaceholder: 'e.g. John Smith',
    existingGuests: 'Existing guests - tap to reuse',
    searchExistingPlaceholder: 'Search by name or phone…',
    change: 'Change',
    sectionBooking: 'Booking',
    submit: 'Create',
    created: 'Reservation created',
    createFailed: 'Failed to create reservation. Please try again.',
    nameRequired: 'Guest name is required.',
    nameInvalid: 'Guest name cannot contain numbers or special characters.',
    emailInvalid:
      'Enter a valid email like name@example.com - no spaces or double dots (..). Or leave it blank.',
    phoneInvalid: 'Enter a valid phone number - 7 to 14 digits, excluding the country code.',
    phoneRequired: 'A phone number is required for phone bookings.',
    timeInPast: 'The reservation time is in the past. Pick a later date or time.',
    timeRequired: 'Enter a time for the reservation.',
    optional: '(optional)',
    noRestaurant: 'No restaurant selected.',
    returningGuest: (n: number) => `${n} previous visit${n === 1 ? '' : 's'}`,
    partySizeOverLimit: (max: number) => `Over the ${max}-guest limit`,
    partySizeOverLimitTitle: 'Large party',
    partySizeOverLimitConfirm: (max: number) =>
      `This booking is above the ${max}-guest limit. Create it anyway?`,
    slotFullWarning: 'This time slot has reached its capacity limit.',
    slotFullTitle: 'Slot full',
    slotFullConfirm: 'This time slot is already at its capacity limit. Create the booking anyway?',
  },

  aiAssistant: {
    title: 'Assistant',
    conversations: 'Conversations',
    archived: 'Archived',
    noConversations: 'No conversations yet.',
    untitled: 'New conversation',
    newChat: 'New chat',
    threadActions: 'Conversation actions',
    loadingConversation: 'Loading conversation…',
    loadHistoryError: 'Could not load this conversation.',
    loadThreadsError: 'Could not load your conversations.',
    retry: 'Retry',
    greetingTitle: 'Hello there',
    greetingBody: 'How can I help you today?',
    attachImage: 'Attach image',
    removeAttachment: 'Remove attachment',
    attachment: 'Image',
    uploading: 'Uploading…',
    uploadFailed: 'Upload failed',
    inputPlaceholder: 'Message the assistant…',
    send: 'Send',
    stop: 'Stop',
    sendFailed: 'The assistant could not answer. Please try again.',
    rename: 'Rename',
    archive: 'Archive',
    unarchive: 'Restore',
    delete: 'Delete',
    cancel: 'Cancel',
    deleteTitle: 'Delete this conversation?',
    threadActionFailed: 'Could not update the conversation.',
    approve: 'Approve',
    deny: 'Deny',
    approvalPending: 'Waiting for your approval',
    approvalDenied: 'Declined',
    approvalRunning: 'Applying…',
    approvalDone: 'Applied',
    approvalFailed: 'Could not apply the change',
    toolWorking: 'Working…',
    toolFailed: 'Failed',
    tools: {
      get_menu: 'Menu',
      get_item_availability: 'Menu availability',
      set_prices: 'Change prices',
      set_item_availability: 'Change availability',
      manage_products: 'Edit products',
      manage_categories: 'Edit categories',
      list_reservations: 'Reservations',
      get_reservation: 'Reservation details',
      check_availability: 'Availability check',
      create_reservation: 'New reservation',
      cancel_reservation: 'Cancel reservation',
      assign_tables: 'Assign tables',
      search_customers: 'Customer search',
      get_customer: 'Customer details',
      create_customer: 'New customer',
      get_opening_hours: 'Opening hours',
      get_profile: 'Restaurant profile',
      update_opening_hours: 'Change opening hours',
      update_profile: 'Update profile',
      toggle_online_ordering: 'Online ordering',
      toggle_table_ordering: 'Table ordering',
      get_table_state: 'Table status',
      list_open_tabs: 'Open tabs',
      get_session: 'Tab details',
      fire_round: 'Fire round',
      void_item: 'Void item',
      take_payment: 'Record payment',
      close_tab: 'Close tab',
      move_session: 'Move tab',
      get_revenue: 'Sales',
      get_covers: 'Covers',
      get_top_dishes: 'Top dishes',
      get_peak_hours: 'Peak hours',
    },
    summary: {
      price: (name, price) => `${name}: ${price}`,
      inStock: (name) => `${name}: available`,
      soldOut: (name) => `${name}: sold out`,
      create: (name) => `Create “${name}”`,
      update: (name) => `Update “${name}”`,
      rename: (from, to) => `Rename “${from}” to “${to}”`,
      remove: (name) => `Delete “${name}”`,
      reservation: (name, date, time, party) => `${name} · ${date} ${time} · ${party} guests`,
      tables: (name, tables) => `${name}: ${tables}`,
      hoursDay: (day, hours) => `${day}: ${hours}`,
      closed: 'closed',
      on: 'On',
      off: 'Off',
      pickup: 'Pickup',
      delivery: 'Delivery',
      table: (label) => `Table ${label}`,
      move: (from, to) => `Table ${from} → table ${to}`,
      cash: 'Cash',
      bankTransfer: 'Bank transfer',
      tip: (amount) => `Tip: ${amount}`,
      profileFields: {
        name: 'Name',
        address: 'Address',
        phone: 'Phone',
        email: 'Email',
        description: 'Description',
      },
    },
    widgets: {
      reservationsTitle: (date) => `Reservations · ${date}`,
      reservationsSummary: (count, guests) => `${count} bookings · ${guests} guests`,
      noReservations: 'No reservations.',
      partySize: (count) => `${count} guests`,
      status: {
        PENDING: 'Pending',
        CONFIRMED: 'Confirmed',
        REJECTED: 'Rejected',
        CANCELLED: 'Cancelled',
      },
      revenueTitle: 'Sales',
      netSales: 'Net sales',
      grossSales: 'Gross sales',
      orders: 'Orders',
      online: 'Online',
      dineIn: 'Dine-in',
      covers: 'Covers',
      tips: 'Tips',
      availabilityTitle: 'Menu availability',
      availabilitySummary: (total, soldOut) => `${total} dishes · ${soldOut} sold out`,
      soldOut: 'Sold out',
      inactive: 'Hidden',
    },
  },

  auth: {
    title: 'Restaurant Dashboard',
    subtitle: 'Sign in to manage your restaurant',
    emailLabel: 'Email',
    emailPlaceholder: 'you@restaurant.com',
    passwordLabel: 'Password',
    passwordPlaceholder: '••••••••',
    signInButton: 'Sign in',
    validation: {
      emailPasswordRequired: 'Enter your email and password.',
      invalidCredentials: "That email or password doesn't work. Please try again.",
    },
    selectRestaurant: {
      title: 'Select restaurant',
      loading: 'Loading your restaurants...',
      empty: 'No restaurants found',
      noAccess: 'Your account has no access to any restaurants.',
      loadError: 'Failed to load user context.',
    },
  },

  customers: {
    title: 'Customers',
    searchPlaceholder: 'Search by name, email, or phone',
    loadingError: 'Could not load customers',
    loadingErrorDescription: 'Check your connection and try again.',
    noMatches: 'No matches',
    noMatchesDescription: 'No customer matches this search.',
    empty: 'No customers yet',
    emptyDescription: 'Customers appear here once they book or order.',
    visitCount: (count) => `${count} visit${count === 1 ? '' : 's'}`,
    create: {
      title: 'New customer',
      firstName: 'First name',
      lastName: 'Last name',
      phone: 'Phone',
      email: 'Email',
      dateOfBirth: 'Date of birth',
      notes: 'Notes',
      required: 'Required',
      optional: 'Optional',
      notesPlaceholder: 'Staff-only note about this customer',
      submit: 'Add customer',
      submitting: 'Adding…',
      fixErrors: 'Please fill in the required fields.',
      requiredError: 'This field is required.',
      invalidEmail: 'Enter a valid email like name@example.com (a complete address).',
      created: 'Customer created',
      createdPhoneError: 'Customer created - phone couldn’t be saved',
      createFailed: 'Couldn’t create customer. Try again.',
    },
    detail: {
      title: 'Customer',
      done: 'Done',
      loadingError: 'Could not load customer',
      profile: 'Profile',
      firstName: 'First name',
      lastName: 'Last name',
      emailLabel: 'Email',
      emailPlaceholder: 'Optional',
      dateOfBirth: 'Date of birth',
      phoneNumbers: 'Phone numbers',
      primary: 'Primary',
      noPhoneNumbers: 'No phone numbers',
      addPhoneButton: 'Add phone number',
      addPhoneDialog: 'Phone number',
      phoneAdded: 'Phone number added',
      removePhoneConfirm: (phone) => `Delete phone number ${phone}?`,
      removePhoneDialog: 'Delete phone number',
      phoneRemoved: 'Phone number deleted',
      primarySet: 'Set as primary',
      removeAction: 'Delete',
      notes: 'Notes',
      notesPlaceholder: 'Internal note about this customer',
      activity: 'Activity',
      totalVisits: 'Total visits',
      lastVisit: 'Last visit',
      status: 'Status',
      active: 'Active',
      activeYes: 'Yes',
      activeNo: 'No',
      customerActivated: 'Customer activated',
      customerDeactivated: 'Customer deactivated',
      name: 'Name',
      invalidValue: 'Invalid value',
      deleteCustomer: 'Delete customer',
      deleteCustomerFallback: 'this customer',
      deleteCustomerConfirm: (who) => `Delete ${who}? This removes them from your customers.`,
      deleteCustomerTitle: 'Delete customer',
      customerDeleted: 'Customer deleted',
    },
  },

  emailFeature: {
    validation: {
      recipientRequired: 'Please enter a recipient email address.',
    },
  },

  giftCards: {
    title: 'Gift cards',
    issueSection: 'Issue gift card',
    startingBalance: (currency) => `Starting balance (${currency})`,
    recipientName: 'Recipient name (optional)',
    recipientNamePlaceholder: 'John Doe',
    issuing: 'Issuing…',
    issueCard: 'Issue card',
    couldntLoadCards: 'Could not load gift cards',
    noCardsYet: 'No gift cards yet',
    noCardsDescription:
      'Issue a prepaid gift card a guest can redeem against a restaurant bill. Tap +.',
    detailTitle: 'Gift card',
    voidCardConfirm: (code) =>
      `Void ${code}? The remaining balance is forfeited and the card can no longer be used.`,
    voidCardTitle: 'Void gift card',
    enterNonZeroAmount: 'Enter a non-zero amount (prefix − to deduct).',
    reasonRequired: 'A reason is required.',
    balanceAdjusted: 'Balance adjusted',
    code: 'Code',
    balance: 'Balance',
    initial: 'Initial amount',
    status: 'Status',
    recipient: 'Recipient',
    adjustBalance: 'Adjust balance',
    voidCard: 'Void card',
    adjustment: (currency) => `Adjustment (${currency}, prefix − to deduct)`,
    adjustmentPlaceholder: '10.00 or -5.00',
    reason: 'Reason',
    reasonPlaceholder: 'Goodwill / correction',
    applying: 'Applying…',
    apply: 'Apply',
    history: 'History',
    enterStartingBalance: 'Enter a starting balance.',
    cardIssued: (code) => `Gift card ${code} issued`,
    couldntLoadCard: 'Could not load card',
  },

  kitchen: {
    title: 'Kitchen',
    sourceOnline: 'Online order',
    sourceDineIn: 'Dine-in',
    bumpServed: 'Served',
    bumpStart: 'Start',
    bumpReady: 'Ready',
    recallAction: 'Recall',
    errorTitle: 'Could not load board',
    emptyTitle: 'All caught up',
    emptyDescription: 'No tickets in the queue.',
    laneCooking: 'Cooking',
    laneReadyToServe: 'Ready to serve',
  },

  loyalty: {
    title: 'Loyalty program',
    pointsBalance: 'Points balance',
    entryType: {
      earned: 'Earned',
      redeemed: 'Redeemed',
      adjusted: 'Adjusted',
    },
    validation: {
      nonZeroAmount: 'Please enter a non-zero amount.',
      reasonRequired: 'A reason is required.',
    },
    pointsAdjusted: 'Points adjusted',
    adjustment: {
      label: 'Adjustment (points, + or −)',
      placeholder: 'e.g. 50 or -20',
    },
    reason: {
      label: 'Reason',
      placeholder: 'Goodwill / correction',
    },
    applyAdjustment: 'Apply adjustment',
    adjustPoints: 'Adjust points',
    couldNotLoad: 'Could not load loyalty settings',
    program: 'Loyalty program',
    earning: {
      title: 'Earning',
      label: (currency) => `${currency} spent - points earned`,
      info: (currency) => `e.g. 1 point per ${currency} on the item subtotal`,
    },
    redemption: {
      title: 'Redemption',
      label: (currency) => `1 point worth (${currency} discount)`,
      placeholder: '0.10',
      info: 'Guests redeem points for a discount at checkout',
    },
    settingsSaved: 'Loyalty settings saved',
    info: {
      note: 'Points are earned on the item subtotal of a completed order or restaurant visit (excluding delivery, taxes, and tips). Redemption is applied as a discount on the next order.',
    },
  },

  menu: {
    title: 'Menu',
    noMenusYet: 'No menus yet',
    createMenuDescription: 'Create a menu to add categories and dishes.',
    createMenu: 'Create menu',
    menuNamePrompt: 'Menu name (English)',
    newMenu: 'New menu',
    menuCreated: 'Menu created',
    renameMenu: 'Rename menu',
    editMenuNameAvailability: 'Edit name & availability',
    renamed: 'Renamed',
    cantDeleteActiveMenu: 'The active menu cannot be deleted',
    deleteMenuConfirm: (menuName) => `Delete "${menuName}" and all its categories and dishes?`,
    deleteMenu: 'Delete menu',
    setAsActiveMenu: 'Set as active menu',
    isNowActive: (menuName) => `"${menuName}" is now active`,
    activeMenuInfo:
      'Only the active menu is shown to customers. Tap a menu to edit its categories and dishes.',
    active: 'Active',
    rename: 'Rename',
    newCategory: 'New category',
    editCategory: 'Edit category',
    categoryName: 'Name',
    categoryDescription: 'Description',
    channelVisibility: 'Show on order channels',
    channelVisibilityHelp:
      'Turn off to hide this whole category (and its items) on that order type.',
    showOnPickup: 'Show on Pickup',
    showOnDelivery: 'Show on Delivery',
    nameRequired: 'Name required',
    availabilitySchedule: 'Availability schedule',
    restrictToCertainTimes: 'Restrict to certain times',
    window: (index) => `Window ${index}`,
    from: 'From',
    to: 'To',
    addWindow: '+ Add window',
    scheduleNote: 'Schedules use the restaurant timezone and are independent of opening hours.',
    emptyMenu: 'Empty menu',
    addCategoryDescription: 'Add a category like Drinks, Mains or Desserts.',
    addCategory: 'Add category',
    dragToReorder: 'Drag to reorder categories',
    renameMenuTooltip: 'Rename menu',
    doneReordering: 'Done reordering',
    reorderSections: 'Reorder categories',
    archivedItems: 'Archived items',
    archivedItemsTitle: 'Archived items',
    archivedItemsEmpty: 'No archived items',
    archivedItemsHint: 'Deleted items are kept here. Restore one to put it back on the menu.',
    restore: 'Restore',
    itemRestored: 'Item restored',
    reorderOptions: 'Reorder options',
    dragToReorderOptions: 'Drag to reorder options',
    deleteMenuConfirmText: (menuName) => `Delete "${menuName}" and everything in it?`,
    inactiveMenuNote: "This menu is not active - customers don't see it yet. Use ⋯ to activate it.",
    menuActivated: 'Menu activated',
    addItem: 'Add item',
    searchItems: 'Search items',
    jumpToSection: 'Jump to section',
    noItemsMatch: 'No items match',
    addMultiple: 'Add several items',
    newProducts: 'New product(s)',
    allergenLabeling: {
      title: 'Allergens & additives',
      sectionTitle: 'Allergen labeling',
      sectionHelp: 'Set up the allergens and additives shown on your dishes.',
      sectionSubtitle: 'Your allergen sheet and code list',
      modeHeading: 'How you label allergens',
      modeNote: 'Choose how this restaurant lists allergens. Switch anytime.',
      aboutMode: (title: string) => `About ${title}`,
      simpleTitle: 'Free sheet',
      simpleFeatures: [
        'Paste your text or upload one photo',
        'Guests see it exactly as written',
        'Simplest — nothing to set up',
      ],
      simpleDetails:
        'Your printed allergen/additive sheet — text or a photo — is shown to guests as the source of truth. Nothing is parsed.',
      advancedTitle: 'Code list',
      advancedFeatures: [
        'A list of your codes and meanings',
        'Dishes tag your codes',
        'Cleaner per-dish labels',
      ],
      advancedDetails:
        'Keep a structured list of your codes (e.g. a = Gluten). Tag each dish with its codes; guests see the code and its meaning.',
      originTextLabel: 'Sheet text or notes',
      originTextPlaceholder: 'Paste the legend as text, or add notes to help read the photo…',
      originImageTitle: 'Sheet photo',
      notesHint: 'Optional. Combine with the photo — e.g. "the S and R icons mean pork and beef".',
      uploadImage: 'Upload photo',
      replaceImage: 'Replace photo',
      removeImage: 'Remove',
      imageFailed: 'Image upload failed',
      tableTitle: 'Code list',
      previewTitle: 'Code preview',
      previewHint: 'From your sheet. Switch to Code list to edit.',
      previewEmptyText:
        'Codes appear here once added in Code list (or read from your sheet in chat).',
      emptyTitle: 'No codes yet',
      keyPlaceholder: 'Code',
      descPlaceholder: 'Description',
      addRow: 'Add code',
      removeRow: 'Remove code',
      saved: 'Saved',
      saveFailed: 'Could not save',
      parseFailed:
        'Saved, but the codes could not be read. Try Save again, or add them in Code list.',
      duplicateKey: (key: string) => `Duplicate code: ${key}`,
      kindLabel: (k: 'ALLERGEN' | 'ADDITIVE' | 'DIETARY'): string =>
        ({ ALLERGEN: 'Allergen', ADDITIVE: 'Additive', DIETARY: 'Dietary' })[k],
    },
    productForm: {
      noAllergenTable: 'No allergen table yet.',
      setUpAllergenTable: 'Set it up',
      searchAllergenCodes: 'Search codes…',
      editAllergenCodes: 'Edit codes',
      general: 'General',
      name: 'Name',
      code: 'Item code',
      editCode: 'Edit item code',
      noCode: 'None',
      codePlaceholder: 'e.g. A12',
      codeTakenBy: (dish: string) => `Code already used by "${dish}"`,
      description: 'Description',
      basePrice: 'Base price',
      newProduct: 'New product',
      editName: 'Edit name',
      editDescription: 'Edit description',
      options: 'Options',
      optionName: 'Option name',
      optionNamePlaceholder: 'e.g. Size, Toppings',
      required: 'Required',
      allowMultiple: 'Allow multiple selections',
      selectionLimits: 'Selection limits',
      minToPick: 'Minimum to pick',
      maxToPick: 'Maximum to pick (blank = no limit)',
      choiceName: 'Choice name',
      choiceNamePlaceholder: 'e.g. Large, Extra cheese',
      extraPrice: 'Extra price',
      defaultSelection: 'Default selection',
      addChoice: 'Add choice',
      deleteChoice: 'Delete choice',
      noChoices: 'No choices yet',
      availability: 'Availability',
      visibleToCustomers: 'Visible to customers',
      hiddenChoicesFooter: "Hidden choices aren't shown to customers",
      hiddenProductsFooter: "Hidden products don't appear on the menu at all",
      inStock: 'In stock',
      outOfStockChoicesFooter: "Out-of-stock choices are shown but can't be ordered",
      outOfStockProductsFooter: "Out-of-stock products are shown but can't be ordered",
      allergens: 'Allergens',
      labels: 'Labels',
      pricing: 'Pricing',
      channelPrice: 'Channel price',
      channelPrices: 'Channel prices',
      dineIn: 'Dine-in',
      pickup: 'Pickup',
      delivery: 'Delivery',
      photo: 'Photo',
      addPhoto: 'Add photo',
      replacePhoto: 'Replace photo',
      removePhoto: 'Remove photo',
      photoTooLarge: 'That image is too large. Please use one under 10 MB.',
      photoWrongType: 'Unsupported image type. Use JPEG, PNG, WebP or HEIC.',
      photoUploadFailed: "Couldn't save the photo. Please try again.",
    },
    hideCategory: 'Hide category',
    showCategory: 'Show category',
    deleteCategory: 'Delete category',
    deleteCategoryConfirm: (categoryName) => `Delete "${categoryName}" and its dishes?`,
    hidden: 'hidden',
    outOfStock: 'Out of stock',
    plusAddItem: '+ Add item',
    newProduct: 'New product',
    optionName: 'Option name',
    optionNamePlaceholder: 'e.g. Size, Toppings',
    required: 'Required',
    allowMultipleSelections: 'Allow multiple selections',
    minimumToPick: 'Minimum to pick',
    maximumToPick: 'Maximum to pick (empty = unlimited)',
    guestsPickRange: 'Guests pick within the min/max range.',
    guestsPickOne: (required) => `Guests pick exactly one${required ? '.' : ' (or none).'}`,
    selectionMode: 'Selection type',
    pickOneMode: 'Single',
    pickManyMode: 'Multiple',
    guestsPickAny: 'Guests can tick any number (or none).',
    newChoice: 'New choice',
    editChoice: 'Edit choice',
    choiceName: 'Choice name',
    choiceNamePlaceholder: 'e.g. Large, Extra cheese',
    extraPrice: 'Extra price',
    defaultSelection: 'Default selection',
    visibleToCustomers: 'Visible to customers',
    inStock: 'In stock',
    allergens: 'Allergens',
    deleteChoice: 'Delete choice',
    deleteChoiceConfirm: (choiceName) => `Delete choice "${choiceName}"?`,
    noChoicesYet: 'No choices yet',
    addChoice: 'Add choice',
    default: 'Default',
    outOfStockChip: 'Out of stock',
    hiddenChip: 'Hidden',
    editOption: 'Edit option',
    deleteOption: 'Delete option',
    deleteOptionConfirm: (optionName) => `Delete option "${optionName}" and its choices?`,
    pricing: 'Pricing',
    basePrice: 'Base price',
    basePriceFooter:
      'The default list price. Channel-specific price lists (delivery/pickup) override it where set.',
    noOverridesYet:
      'Per-channel price lists (delivery, pickup...) are configured separately; this product has no overrides yet.',
    noDescription: 'No description',
    general: 'General',
    options: 'Options',
    noOptionsYet: 'No options yet - this product is ordered as is.',
    addOption: '+ Add option',
    newOptions: 'New options',
    newChoices: 'New choices',
    saveAllOptions: 'Save all',
    optionsSaved: 'Options saved',
    optional: 'Optional',
    optionNeedsChoice: 'Add at least one choice before saving this option.',
    optionEditHint: 'A choice with no price adds no extra charge.',
    optionLimits: {
      pickSeveral: 'Pick several',
      limitHowMany: 'Limit how many',
      minimum: 'Minimum',
      maximum: 'Maximum',
      minPrompt: (lo: number, hi: number) => `How many must guests pick at least? (${lo} to ${hi})`,
      maxPrompt: (lo: number, hi: number) => `How many can guests pick at most? (${lo} to ${hi})`,
      notSaved: 'Not saved',
      notInteger: 'Not saved. Enter a whole number.',
      outOfRange: (field: string, lo: number, hi: number) =>
        `Not saved. ${field} must be ${lo} to ${hi}.`,
      minAboveMax: (max: number) => `Not saved. Minimum cannot be more than Maximum (${max}).`,
      maxBelowMin: (min: number) => `Not saved. Maximum cannot be less than Minimum (${min}).`,
      summary: {
        exactly: (n: number) => `Guests must pick exactly ${n}`,
        upTo: (n: number) => `Guests pick up to ${n}`,
        range: (min: number, max: number) => `Guests pick ${min} to ${max}`,
        atLeast: (n: number) => `Guests pick at least ${n}`,
        any: 'Guests can pick any number, or none',
      },
      phrase: {
        exactly: (n: number) => `choose ${n}`,
        upTo: (n: number) => `up to ${n}`,
        range: (min: number, max: number) => `choose ${min}-${max}`,
        atLeast: (n: number) => `choose at least ${n}`,
        any: 'choose any',
      },
      stockWarning: (inStock: number, total: number) =>
        `Only ${inStock} of ${total} in stock - guests cannot order this until restocked.`,
      legacy: {
        minAboveN: (min: number, n: number) =>
          `Minimum (${min}) is more than the ${n} choices in this group.`,
        maxAboveN: (max: number, n: number) =>
          `Maximum (${max}) is more than the ${n} choices in this group.`,
        minAboveMax: (min: number, max: number) =>
          `Minimum (${min}) is more than Maximum (${max}).`,
      },
      fixRule: 'Fix rule',
      clampTitle: 'Lower the limit?',
      clampBody: (option: string, choice: string, n: number, rule: string) =>
        `${option}: ${rule}. Without ${choice} only ${n} choices are left, so`,
      clampChange: {
        min: (n: number) => `Minimum changes to ${n}.`,
        max: (n: number) => `Maximum changes to ${n}.`,
        both: (n: number) => `Minimum and Maximum change to ${n}.`,
      },
      lowerAndDelete: (n: number) => `Lower to ${n} and delete`,
    },
    optionNameRequiredAll: 'Please enter a name for every option before saving.',
    optionNameDuplicate: (name: string) =>
      `Option "${name}" has a duplicate name. Each option must have a different name.`,
    choiceNameRequiredAll: 'Please enter a name for every choice before saving.',
    choiceNameDuplicate: (name: string) =>
      `Choice "${name}" has a duplicate name. Each choice must have a different name.`,
    addOptionHint:
      'Each option is a group of choices (e.g. Size, Topping). Add several groups then tap Save all; add the values inside each after saving.',
    availability: 'Availability',
    visibleToCustomersFooter: 'Hidden products do not appear in the menu at all',
    inStockFooter: 'Unavailable products are shown but cannot be ordered',
    labels: 'Labels',
    editName: 'Edit name',
    editDescription: 'Edit description',
    createProduct: 'Create product',
    productCreated: 'Product created',
    missingCategory: 'Missing category - open this from a menu category.',
    createProductNoteFooter:
      'You can add a description, allergens, labels and options after creating.',
    deleteProduct: 'Delete product',
    deleteProductConfirm: (productName) => `Delete "${productName}" and everything in it?`,
    product: 'Product',
    productNotFound: 'Product not found.',
    englishNameRequired: 'Name required.',
    englishValueRequired: 'English Value required',
    fieldRequired: 'This field is required',
    newOption: 'New option',
    channelPriceEditTitle: (channel: string) => `${channel} price`,
    channelPricesFooter:
      'Dine-in is the default price. Leave pickup or delivery blank to use it, or enter an amount to charge differently for that channel.',
    pricesSaved: 'Prices saved',
    overridesBaseFooter: (base: string) =>
      `Overrides the base price (${base}) for this channel only. Clear it to fall back to the base.`,
    useBasePrice: 'Use base price',
    basePriceSectionFooter:
      'The default price, plus optional separate prices for pickup and delivery.',
    priceFree: 'Free',
    failedToLoad: 'Failed to load menu',
    notLiveAnywhereNote:
      "This menu isn't live on any order channel - customers don't see it yet. Choose it for a channel under Menu → Live menus.",
    itemColumn: 'Item',
    priceColumn: 'Price',
    priceFrom: 'from',
    optionsCount: (n: number) => `${n} option${n === 1 ? '' : 's'}`,
    allMenus: 'All menus',
    allMenusHelp:
      'The panel above picks which menu each channel shows now. Tap a menu to edit its categories & dishes.',
    editMenu: 'Edit menu',
    liveMenus: 'Live menus',
    liveMenu: 'Live menu',
    differentMenuPerChannel: 'Different menu per channel',
    assignMenuPerChannelHelp: 'Assign the menu each order channel shows.',
    liveMenuHelp: 'The menu customers see on dine-in, pickup and delivery.',
    menuFieldLabel: 'Menu',
    mixed: 'Mixed',
    noneHidden: 'None - hidden',
    noSelection: 'None',
    selectedCount: (n: number) => `${n} selected`,
    category: 'Category',
    category: 'Category',
    items: 'Items',
    addCategoryFirst: 'Add a category first.',
    addCategoryFirstThenItems: 'Add a category first, then add items to it.',
    itemNameRequiredForPrice: 'Name is required for every item with a price.',
    enterAtLeastOneItem: 'Enter at least one item name.',
    itemsAddedToast: (n: number) => `${n} item${n === 1 ? '' : 's'} added`,
    itemNamePlaceholder: 'Item name *',
    basePriceAriaLabel: (currency: string) => `Base price (${currency})`,
    removeItem: 'Remove',
    addRow: 'Add row',
    addAnotherItem: 'Add another item',
    simpleMode: 'Multiple items',
    advancedMode: 'One detailed item',
    groupName: 'Group name',
    requiredGroupDefault: 'Required',
    requiredChoicesHint: 'Pick at least 1',
    optionalChoicesHint: 'Optional',
    optionalGroupDefault: 'Optional',
    dayLabelsShort: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  },

  menuQuickEdit: {
    placeholder: 'Ask AI, e.g. “raise pizzas by 5%”',
    fullMenu: 'Full menu',
    productPlaceholder: 'Ask AI, e.g. “add extra cheese for 1.50”',
    tagNew: 'New',
    tagRemoved: 'Removed',
    tagMoved: 'Moved',
    shown: 'Shown',
    hidden: 'Hidden',
    inStock: 'In stock',
    soldOut: 'Sold out',
    isDefault: 'Default',
    notDefault: 'Not default',
    basePrice: 'base price',
    hours: 'Hours',
    always: 'Always',
    allergens: 'Allergens',
    labels: 'Labels',
    order: 'Order',
    pickup: 'Pickup',
    delivery: 'Delivery',
    none: 'none',
    rules: {
      required: 'required',
      optional: 'optional',
      pickOne: 'pick 1',
      pickAny: 'pick any',
      pickUpTo: (n: number) => `pick up to ${n}`,
      pickAtLeast: (n: number) => `pick at least ${n}`,
      pickRange: (min: number, max: number) => `pick ${min}–${max}`,
    },
    summary: {
      items: (n: number) => `${n} item${n === 1 ? '' : 's'}`,
      sections: (n: number) => `${n} section${n === 1 ? '' : 's'}`,
      prices: (n: number) => `${n} price${n === 1 ? '' : 's'}`,
      added: (n: number) => `${n} added`,
      removed: (n: number) => `${n} removed`,
      renamed: (n: number) => `${n} renamed`,
      descriptions: (n: number) => `${n} description${n === 1 ? '' : 's'}`,
      moved: (n: number) => `${n} moved`,
      stock: (n: number) => `${n} stock change${n === 1 ? '' : 's'}`,
      visibility: (n: number) => `${n} shown or hidden`,
      allergens: (n: number) => `${n} allergen update${n === 1 ? '' : 's'}`,
      labels: (n: number) => `${n} label update${n === 1 ? '' : 's'}`,
      hours: (n: number) => `${n} schedule${n === 1 ? '' : 's'}`,
      rules: (n: number) => `${n} group rule${n === 1 ? '' : 's'}`,
      defaults: (n: number) => `${n} default${n === 1 ? '' : 's'}`,
    },
  },

  promotionsQuickEdit: {
    placeholder: 'Ask AI, e.g. “10% off pickup orders Tue–Thu after 17:00”',
    restoredTag: 'Restored',
    summary: {
      created: (n: number) => `${n} new`,
      updated: (n: number) => `${n} changed`,
      archived: (n: number) => `${n} archived`,
      restored: (n: number) => `${n} restored`,
    },
    conflict: {
      replaces: (example: string, other: string) => `${example}: replaces “${other}”`,
      stacks: (example: string, other: string) => `${example}: adds up with “${other}”`,
      loses: (example: string, other: string) =>
        `${example}: “${other}” gives more, so this one does not apply`,
    },
  },

  exceptionsQuickEdit: {
    placeholder: 'Ask AI, e.g. “close for Christmas”',
    summary: {
      created: (n: number) => `${n} new`,
      updated: (n: number) => `${n} changed`,
      removed: (n: number) => `${n} removed`,
    },
  },

  aiChangeset: {
    propose: 'Draft changes',
    proposing: 'Drafting changes…',
    previousChange: 'Previous change',
    nextChange: 'Next change',
    changesOnly: 'Changes only',
    apply: 'Apply',
    discard: 'Discard',
    staleNotice: (items: string) => `Changed in the meantime, check again: ${items}`,
    appliedToast: (n: number) => `${n} change${n === 1 ? '' : 's'} applied`,
    appliedPartialToast: (applied: number, total: number) =>
      `Only ${applied} of ${total} changes applied, check the rest`,
    undo: 'Undo',
    undoneToast: 'Changes undone',
    undoStale: 'Nothing was undone: some items changed in the meantime.',
  },

  aiDock: {
    menuTitle: 'Menu AI',
    exceptionsTitle: 'Holidays AI',
    promotionsTitle: 'Discounts AI',
    productTitle: 'Dish AI',
    sharedWithTeam: 'Shared with your team',
    expand: 'Show AI conversation',
    collapse: 'Hide AI conversation',
    attach: 'Attach a photo or PDF',
    send: 'Send',
    removeAttachment: 'Remove attachment',
    uploading: 'Uploading…',
    readingStart: 'Reading your menu…',
    readingPage: (page: number, total: number) => `Reading page ${page} of ${total}…`,
    dishesSoFar: (n: number) => `${n} ${n === 1 ? 'dish' : 'dishes'} so far · you can leave`,
    importFailed: "Couldn't read the menu.",
    pagesNotRead: (pages: string) =>
      `Couldn't read: ${pages}. Attach that page and ask AI to add its dishes.`,
    lockedByOther: 'Someone else is editing with AI. Try again in a moment.',
    loadOlder: 'Show earlier messages',
    emptyHistory:
      'Ask AI to change prices, add dishes or fix names. Your team sees this conversation.',
    teammate: 'Teammate',
    menuCreated: 'Menu created',
    deleteMenuConfirm: 'Delete this menu?',
    deleteMenu: 'Delete',
    tryAgain: 'Try again',
    undoCreated: 'Undo (delete menu)',
    changesApplied: (n: number) => `${n} ${n === 1 ? 'change' : 'changes'} applied`,
    changesPartlyApplied: (applied: number, missing: number) =>
      `${applied} applied, ${missing} did not apply`,
    applyingChanges: (n: number) => `Applying ${n} ${n === 1 ? 'change' : 'changes'}…`,
    reviewChanges: (n: number) => `Review ${n} ${n === 1 ? 'change' : 'changes'}`,
    discarded: 'Discarded',
    undone: 'Undone',
    changesPosition: (position: string, total: number) =>
      `${position} / ${total} ${total === 1 ? 'change' : 'changes'}`,
  },

  menuCreate: {
    title: 'Create with AI',
    nameLabel: 'Menu name',
    namePlaceholder: 'Leave empty, AI suggests one',
    sources: 'Your menu',
    sourcesHelp: 'Photos or PDFs of your menu, or a link to it.',
    camera: 'Take photo',
    addFiles: 'Add photos or PDFs',
    removeSource: 'Remove',
    linkLabel: 'Link',
    linkPlaceholder: 'https://your-restaurant.com/menu',
    invalidLink: 'Enter a full link starting with https://',
    hintLabel: 'Anything the AI should know?',
    hintPlaceholder: 'e.g. “Only the lunch menu, skip the drinks page”',
    keepItemNumbers: 'Keep item numbers',
    offNote: 'The menu stays off until you publish it.',
    create: 'Create menu',
    unsupportedFiles: (names: string) => `Can't use ${names}. Use PNG, JPEG, WebP or PDF.`,
    tooManySources: (max: number) => `Up to ${max} files`,
  },

  menuChecklist: {
    toCheck: (n: number) => `${n} to check`,
    noPriceCount: (n: number) => `${n} no price`,
    unknownAllergensCount: (n: number) => `${n} unknown ${n === 1 ? 'allergen' : 'allergens'}`,
    noPrice: 'No price',
    addLegend: (n: number) => `Add ${n} ${n === 1 ? 'code' : 'codes'} from the menu legend`,
    legendIntro: 'These codes are printed on your menu but missing from your allergen list.',
    addCodes: 'Add to allergen list',
    unknownCode: (code: string) => `'${code}' not in allergen list`,
  },

  menuImportReview: {
    title: 'Check the import',
    open: 'Check against the original',
    openFlagged: (n: number) => `Check against the original · ${n} flagged`,
    page: (n: number, total: number) => `Page ${n} of ${total}`,
    previousPage: 'Previous page',
    nextPage: 'Next page',
    flaggedOnly: 'Flagged only',
    allDishes: 'All dishes',
    showPage: 'Original',
    showDishes: 'Dishes',
    flaggedCount: (n: number) => `${n} flagged`,
    noDishes: 'No dishes were read from this page.',
    noFlagged: 'Nothing flagged on this page.',
    sourceMissing: 'The original file is no longer available.',
    openOriginal: 'Open original',
    unavailableTitle: 'Nothing to check against',
    unavailableText:
      'This menu has no stored source pages. Menus imported before this check existed cannot be reviewed here.',
    liveNote: 'This menu is already live. Flags are shown for reference.',
    noPrice: 'No price',
    priceOutlier: (typical: string) =>
      `Price far from the rest of the section (typical ${typical})`,
    unknownCodes: (codes: string) => `Not in your allergen list: ${codes}`,
    itemNumberDropped: (code: string) => `Item number ${code} was used twice and left out`,
    pickLimit: (limits: string) => `Guests must pick an exact number: ${limits}`,
  },

  notificationsFeature: {
    couldntLoadNotifications: "Couldn't load notifications",
    allCaughtUp: 'All caught up',
    noNotifications: 'No notifications.',
    today: 'Today',
    earlier: 'Earlier',
    markAllRead: 'Mark all as read',
    notificationsSheet: 'Notifications',
  },

  openingHours: {
    editClosure: 'Edit closure',
    addClosure: 'Add closure',
    reason: 'Reason',
    reasonPlaceholder: 'E.g. Christmas Day, staff party',
    appliesToLabel: 'Applies to',
    allServices: 'All services',
    servicesSelected: (n: number) => `${n} services`,
    pickAtLeastOneService: 'Choose at least one service',
    dineIn: 'Dine-in',
    pickup: 'Pickup',
    delivery: 'Delivery',
    reservation: 'Reservation',
    whatHappens: 'What happens',
    whatHappensHint:
      'Holiday closes the venue all day. Exception keeps it open with different hours on these dates - for example opening later or closing earlier.',
    holiday: 'Holiday',
    exception: 'Exception',
    closedAllDay: 'Closed all day',
    specialHours: 'Special hours',
    dates: 'Dates',
    from: 'From',
    to: 'To',
    startDateAriaLabel: 'Start date',
    endDateAriaLabel: 'End date',
    singleDayHint: 'A single day? Set From and To to the same date.',
    shiftLabel: (number) => `Shift ${number}`,
    openLabel: 'Open',
    removeHoursAriaLabel: 'Remove hours',
    addHours: 'Add hours',
    hoursReplaceWeeklyNote:
      'These hours replace the normal weekly opening hours on the selected dates.',
    pickStartEndDate: 'Pick a start and end date.',
    endDateBeforeStart: 'The end date cannot be before the start date.',
    addSpecialHours: 'Add at least one set of special hours.',
    checkSpecialHours: 'Check the special hours - open must be before close.',
    closureUpdated: 'Closure updated',
    closureAdded: 'Closure added',
    couldNotSave: "Couldn't save - try again.",
    holidaysClosures: 'Holidays & closures',
    removeClosureConfirm: 'Remove closure',
    removeClosureQuestion: (label) => `Remove closure for ${label}?`,
    closureRemoved: 'Closure removed',
    couldNotRemove: "Couldn't remove - try again.",
    couldNotLoadClosures: "Couldn't load closures",
    noClosuresYet: 'No closures yet',
    noClosuresDescription:
      'Add a holiday or a day with special hours to override the weekly schedule. Tap +.',
    closedBadge: 'Closed',
    specialBadge: 'Special',
    closuresOverrideNote:
      'Closures override the weekly opening hours. A closed day removes online-ordering and reservation slots; special hours replace them for the selected dates.',
    title: 'Opening hours',
    service: 'Service',
    hoursFor: 'Hours for',
    hoursServiceNote:
      'Each service keeps its own weekly opening hours. Pickup and delivery hours control the online-ordering page; reservation hours control the booking calendar.',
    oneOffClosedDays: 'One-off closed days and special hours',
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
    hoursLabel: 'Opening hours',
    hours: 'Hours',
    generalHoursNote:
      "These are the restaurant's general opening hours. Reservation, pickup and delivery follow them automatically - give a channel its own hours from its settings if it differs. One-off holidays and special-hours days live under Holidays & Closures.",
    openTimeAria: (label) => `${label} open`,
    closeTimeAria: (label) => `${label} close`,
    enabledWithoutHours: (label) => `${label} is on but has no hours - add a time or turn it off`,
    checkTimes: (label) => `Check the times for ${label}`,
    noOvernightOrders: (label) =>
      `${label}: closing time must be after opening - order hours can't run past midnight.`,
    openAfterClose: (label) => `${label}: opening time can't be after closing time.`,
    shiftsOverlap: (label) =>
      `${label}: time ranges overlap - adjust them so they don't intersect.`,
    invalidHoursTitle: 'Invalid hours',
    openingSaved: 'Opening hours saved',
    saveFailed: 'Save failed',
    couldNotLoadHours: "Couldn't load opening hours",
    checkConnection: 'Check your connection and try again.',
    saveButtonLabel: (service) => `Save ${service} opening hours`,
    followingTheseHours: 'Following these hours',
    setCustomHours: 'Set custom hours',
    nowFollowingGeneral: 'Now following general hours',
    confirmFollowTitle: 'Switch to general hours?',
    confirmFollowMessage:
      "This deletes this channel's custom weekly hours. It will follow the restaurant's general hours instead.",
    failedSetCustom: 'Failed to set custom hours',
    failedUpdate: 'Failed to update',
    orderHoursTitle: 'Order Hours',
    pickupHoursTitle: 'Pickup Hours',
    deliveryHoursTitle: 'Delivery Hours',
    reservationHoursTitle: 'Reservation Hours',
    pickupFollowNote:
      "Pickup follows the restaurant's general opening hours. Turn on custom hours to set when pickup orders are accepted separately.",
    deliveryFollowNote:
      "Delivery follows the restaurant's general opening hours. Turn on custom hours to set when delivery runs separately.",
    reservationFollowNote:
      "Reservations follow the restaurant's general opening hours. Turn on custom hours to set when tables can be booked separately.",
    openingTime: 'Opening time',
    deleteThisClosure: 'Delete this closure',
    removeThisClosureQuestion: 'Remove this closure?',
    statusOpenUntil: (time) => `Open · until ${time}`,
    statusClosedOpensAt: (time) => `Closed · opens ${time}`,
    statusClosedOpensDay: (day, time) => `Closed · opens ${day} ${time}`,
  },

  orderSetup: {
    title: 'Order settings',
    orderTypes: {
      title: 'Order types',
      noConfig: 'No order types configured.',
      footer:
        'Tap a channel to turn it on/off or toggle cash and card payments. Turning a channel off hides it completely from customers - to pause it briefly instead, use availability below.',
      cashStatus: (status, cardStatus) => `Cash ${status} · Card ${cardStatus}`,
      cashOnly: (status) => `Cash ${status}`,
      disabled: "Customers can't order this way",
      on: 'On',
      off: 'Off',
      actionLabel: (label) => `${label} settings`,
      turnOff: (label) => `Turn off ${label}`,
      turnOn: (label) => `Turn on ${label}`,
      stopCash: 'Stop accepting cash',
      acceptCash: 'Accept cash',
      stopCard: 'Stop accepting card payments',
      acceptCard: 'Accept online card payments',
      turnedOff: (label) => `${label} turned off`,
      turnedOn: (label) => `${label} turned on`,
      cashOff: 'Cash payments disabled',
      cashOn: 'Cash payments enabled',
      onlineCardOff: 'Online card payments disabled',
      onlineCardOn: 'Online card payments enabled',
    },
    minimumOrder: {
      title: 'Minimum order',
      noConfig: 'No order types configured.',
      smallestBasket: 'Smallest basket allowed',
      noMinimum: 'No minimum',
      footer:
        'The smallest basket total a customer can check out with, per channel. Set to 0 for no minimum.',
      prompt: (orderType) => `Minimum order total ${orderType} (0 = no minimum)`,
      set: (orderType, amount) => `${orderType} minimum order set to ${amount}`,
      removed: (orderType) => `${orderType} minimum order removed`,
    },
    availability: {
      title: 'Availability',
      accepting: 'Accepting orders',
      pausedUntilClose: 'Paused until close',
      pausedResumes: (time) => `Paused · resumes at ${time}`,
      paused: 'Paused',
      live: 'Live',
      footer:
        'Live status of accepting new orders. Tap a channel to pause it during rushes or kitchen issues (timed / until close) or to resume it. This is independent of enabling the order type above - existing orders are not affected.',
      orderingLabel: (label) => `${label} ordering`,
      pause30: 'Pause for 30 minutes',
      pause1Hour: 'Pause for 1 hour',
      pauseUntilClose: 'Pause until close',
      resumeNow: 'Resume now',
      pausedFor: (label, minutes) => `${label} paused for ${minutes} minutes`,
      pausedUntilCloseToast: (label) => `${label} paused until close`,
      resumed: (label) => `${label} resumed`,
    },
    deliveryZones: {
      title: 'Delivery zones',
      noZones: 'No delivery zones - delivery is unavailable until at least one zone is added.',
      mapArea: 'Map area',
      within: (km) => `Within ${km} km`,
      noRangeSet: 'No range set',
      addZone: '+ Add delivery zone',
      footer:
        'Each zone defines its reach and its own delivery fee and minimum order. Customers see the smallest matching zone.',
      deleteConfirm: (label) => `Delete zone "${label}"?`,
      deleteTitle: 'Delete zone',
      deleted: 'Zone deleted',
    },
    deliveryZone: {
      newZone: 'New delivery zone',
      editZone: 'Edit zone',
      nameLabel: 'Zone name',
      namePlaceholder: 'e.g. City centre, up to 5 km',
      deliveryArea: 'Delivery area',
      mapDrawn: 'Map-drawn',
      mapDrawnFooter: "This area was drawn on a map and can't be edited here.",
      distanceLabel: 'Delivers within (km)',
      distancePlaceholder: 'e.g. 5',
      feeLabel: (currency) => `Delivery fee (${currency})`,
      minOrderLabel: (currency) => `Minimum order (${currency})`,
      minOrderPlaceholder: '0 = no minimum',
      nameRequired: 'Give the zone a name.',
      distanceRequired: 'Enter how far this zone delivers (km).',
      added: 'Zone added',
      updated: 'Zone updated',
      saveFailed: 'Failed to save zone',
    },
    paymentMethods: {
      title: 'Manual payment methods',
      noMethods: 'No manual payment methods - only the default options are offered at checkout.',
      active: 'Active',
      inactive: 'Inactive',
      addMethod: '+ Add payment method',
      footer:
        'Options to pay on arrival or by bank transfer are shown at checkout. Customers follow the instructions and staff confirm the receipt manually.',
      deleteConfirm: (name) => `Delete method "${name}"?`,
      deleteTitle: 'Delete',
      deleted: 'Method deleted',
    },
    paymentMethod: {
      newMethod: 'New payment method',
      editMethod: 'Edit method',
      nameLabel: 'Method name',
      namePlaceholder: 'e.g. Bank transfer, PayPal',
      instructionsLabel: 'Instructions',
      instructionsPlaceholder: 'What the customer should do to pay',
      shownAtCheckout: 'Shown at checkout',
      detail: (number) => `Detail ${number}`,
      remove: 'Remove',
      detailLabel: 'Label',
      detailLabelPlaceholder: 'e.g. IBAN',
      detailValue: 'Value',
      detailValuePlaceholder: 'e.g. DE89 3704 0044 0532 0130 00',
      addDetail: '+ Add account detail',
      detailLabelInfo:
        'Name of the field the customer sees (e.g. IBAN, Account holder, Bank name).',
      detailValueInfo: 'The actual detail the customer copies to pay (e.g. DE89 3704…, ACME Ltd).',
      detailsFooter:
        'Account details are shown as labelled rows below the instructions at checkout - use them for IBAN, account holder, PayPal address, etc.',
      nameRequired: 'Give the method a name.',
      instructionsRequired: 'Add the instructions the customer should follow.',
      detailsInvalid: 'Each account detail needs both a label and a value.',
      added: 'Method added',
      updated: 'Method updated',
      saveFailed: 'Failed to save method',
    },
    shifts: {
      title: 'Order hours',
      pickup: 'Pickup',
      delivery: 'Delivery',
      capacityMode: 'Capacity mode',
      simple: 'Simple',
      simpleDescription: 'One number: how many orders you can take per hour.',
      simpleConfirm: 'Switch capacity to simple mode?',
      simpleDetails:
        'A slot is a 60-minute window - the 7:00 PM slot counts every order scheduled between 7:00 PM and 7:59 PM.\n\nThe default cap applies when no special-hours window covers the time. Leave it empty if no cap is needed.',
      advanced: 'Advanced',
      advancedDescription: 'Choose the slot length and an order cap per slot.',
      advancedConfirm: 'Switch capacity to advanced mode?',
      advancedDetails:
        'Set your own slot length (e.g. 30 min) and an order cap per slot - finer-grained than the simple hourly model.\n\nThe default cap applies when no special-hours window covers the time. Leave it empty if no cap is needed.',
      aboutMode: (mode) => `About ${mode} mode`,
      defaultCapacity: 'Default capacity',
      ordersPerSlot: 'Orders per slot',
      maxOrdersPerHour: 'Max orders per hour',
      noCap: 'no cap',
      slotLength: 'Slot length (min)',
      slotLengthPlaceholder: '30',
      kitchenPrepTime: 'Kitchen prep time (min)',
      noPrepPlaceholder: 'no prep',
      minNoticeLabel: 'Minimum notice for pre-orders (min)',
      nonePlaceholder: 'none',
      defaultFooter:
        'Online orders follow your opening hours. Add special hours below for different times on specific days.',
      specialHours: 'Special hours',
      noSpecialHours: 'None - the default applies daily.',
      setDefaultFirst: 'Set the default capacity first',
      capacityModeSet: (mode) => `Capacity mode: ${mode}`,
      switchModeFailed: 'Failed to switch capacity mode',
    },
    shiftForm: {
      newSpecialHours: 'New special hours',
      editSpecialHours: 'Edit special hours',
      name: 'Name',
      namePlaceholder: 'e.g. Christmas Eve, Sunday hours',
      days: 'Days',
      hours: 'Hours',
      opens: 'Opens',
      closes: 'Closes',
      capacity: 'Capacity',
      preview: 'Preview',
      active: 'Active',
      activeFooter: 'Turn off to keep this rule but stop it applying for now.',
      daysFooter: (channel) =>
        `On these days this rule replaces the normal ${channel} hours. Other days stay unchanged.`,
      capacityFooter:
        "Grey is your default - leave it empty to keep it, or enter a number to use a different value on this rule's days.",
      deleteRule: 'Delete this rule',
      nameRequired: 'Give this rule a name',
      dayRequired: 'Pick at least one day',
      timeInvalid: 'Opens must be before Closes',
      capacityInvalid: 'Capacity must be a positive whole number (or blank for the default)',
      slotInvalid: 'Slot length must be a positive whole number',
      leadInvalid: 'Prep / notice must be whole numbers (or blank to use the default)',
      saveFailed: "Couldn't save",
      deleteConfirm: 'Delete this rule?',
      deleted: 'Deleted',
      deleteFailed: "Couldn't delete",
    },
    holidayForm: {
      newTitle: 'New holiday hours',
      editTitle: 'Edit holiday hours',
      namePlaceholder: 'e.g. Christmas Eve, New Year',
      dates: 'Dates',
      datesFooter: (channel) =>
        `On these dates this holiday replaces BOTH special hours and normal ${channel} hours.`,
      starts: 'Starts',
      startAria: 'Holiday start date',
      ends: 'Ends',
      endAria: 'Holiday end date',
      on: 'On',
      activeFooter: 'Turn off to keep this holiday but stop it overriding for now.',
      deleteHoliday: 'Delete this holiday',
      nameRequired: 'Give this holiday a name',
      dateRangeInvalid: 'End date must be on or after the start date',
      hoursInvalid: 'Opens and Closes must be set and differ',
      capacityInvalid: 'Orders per slot must be a positive whole number (or blank for no cap)',
      prepNoticeInvalid: 'Prep and notice must be whole numbers (0 or more)',
      deleteConfirm: 'Delete this holiday?',
    },
    leadTime: {
      orderNow: 'Order now (as soon as possible)',
      orderLabel: 'Order',
      readyLabel: 'Ready',
      deliveredLabel: 'Delivered',
      deliveryDescription: (mins: number) =>
        `Delivery adds ${mins} min on top of prep — that's when the guest actually has the food.`,
      deliveryRangeDescription: (min: number, max: number) =>
        `Delivery adds ${min}–${max} min on top of prep, depending on the area — that's when the guest actually has the food.`,
      deliveryLiveDescription:
        'Delivery time is calculated per address from live driving data, so it varies with each order.',
      readyDescription:
        "Ready = order time + prep time. This is the customer's wait - where prep time matters most. Minimum notice does not apply.",
      noPrepBuffer: 'No prep buffer - order is ready immediately.',
      orderLater: 'Order later (pre-order)',
      earliestSlot: 'Earliest slot',
      minNoticeDescription: (time) =>
        `Minimum notice - the earliest future slot a customer can pick is ${time}.`,
      noNotice: 'No notice - customers can pick the next slot.',
      opensLabel: 'Opens',
      firstSlotLabel: 'First slot',
      openingBufferDescription: (open: string, first: string) =>
        `Earliest after opening - when ordering opens at ${open}, the first slot a customer can pick is ${first}.`,
      noOpeningBuffer: 'No opening buffer - customers can pick the opening time itself.',
      kitchenStarts: 'Kitchen starts',
      readySlot: 'Ready · slot',
      driverLeaves: 'Driver leaves',
      deliveredSlot: 'Delivered',
      kitchenStartsDeliveryDescription: (slot: string, startTime: string) =>
        `For a ${slot} delivery the kitchen starts at ${startTime} — the drive is subtracted from the slot, not added to it, so the guest still gets it at ${slot}. Shown for your furthest area.`,
      kitchenStartsDeliveryLive:
        'With live driving distance the drive is subtracted per address, so the kitchen start shifts with each order.',
      kitchenStartsDescription: (time, startTime) =>
        `For a selected ${time} slot, the kitchen starts at ${startTime} - the customer still gets it exactly at ${time}.`,
    },
    page: {
      pickup: 'Pickup',
      delivery: 'Delivery',
      noConfig: 'No order types configured.',
      orderTypesTitle: 'Order types',
      canOrder: 'Customers can order this way',
      cannotOrder: 'Customers cannot order this way',
      readOnlyNotice: 'You can view these settings. Only an owner or manager can change them.',
      orderTypesFooter:
        'Turn a channel on or off. Off hides it from customers entirely - to pause it briefly instead, use Availability below.',
      toggledOn: (label) => `${label} turned on`,
      toggledOff: (label) => `${label} turned off`,
      cardTitle: 'Card payments',
      acceptingCard: 'Accepting card online',
      cardOff: 'Card off',
      cardFooter: 'Accept card payments online, per channel.',
      cardOnToast: (label) => `${label} card on`,
      cardOffToast: (label) => `${label} card off`,
      cardOnHandoffTitle: 'Card at handoff',
      acceptingCardOnHandoff: 'Card at handoff on',
      cardOnHandoffOff: 'Card at handoff off',
      cardOnHandoffHelp:
        'The customer pays by card on your POS terminal at pickup or delivery. No online provider needed - you collect and settle it yourself, like cash.',
      cardOnHandoffOnToast: (label) => `${label} card at handoff on`,
      cardOnHandoffOffToast: (label) => `${label} card at handoff off`,
      autoConfirmTitle: 'Auto-confirm',
      autoConfirmOn: 'Cash & card-at-handoff auto-confirmed',
      autoConfirmOff: 'Confirm manually',
      autoConfirmHelp:
        'Cash and card-at-handoff orders are confirmed automatically and the kitchen ticket prints on arrival - no need to tap Accept. Online orders already confirm on payment; bank transfer still needs your review.',
      autoConfirmOnToast: (label) => `${label} auto-confirm on`,
      autoConfirmOffToast: (label) => `${label} auto-confirm off`,
      autoCompleteTitle: 'Automatically complete orders',
      autoCompleteOn: (minutes) => `Closes ${minutes} min after the promised time`,
      autoCompleteOff: 'You mark each order complete yourself',
      autoCompleteHelp:
        'Orders you never closed are marked complete for you a while after the time you promised the customer. Turn this off if you press Complete yourself - then orders stay open until you do.',
      autoCompleteOnToast: (label) => `${label} orders complete automatically`,
      autoCompleteOffToast: (label) => `${label} orders stay open until you complete them`,
      autoCompleteSaveFailed: 'Could not save the delay. Please try again.',
      autoCompleteDelayTitle: 'Wait before completing',
      autoCompleteDelayFooter:
        'How long after the promised time an order is closed for you, in minutes.',
      availabilityTitle: 'Availability',
      accepting: 'Accepting orders',
      pausedUntilClose: 'Paused until close',
      pausedResumes: (time) => `Paused · resumes ${time}`,
      paused: 'Paused',
      live: 'Live',
      disabled: 'Off',
      availabilityFooter:
        'Live status of new-order intake. Tap a channel to pause during a rush or kitchen issue (timed / until close) or resume. This is separate from enabling the order type above - existing orders are never affected.',
      orderingLabel: (label) => `${label} ordering`,
      pause30: 'Pause 30 minutes',
      pause1Hour: 'Pause 1 hour',
      pauseHours: (n) => `Pause ${n} hours`,
      pauseUntilClose: 'Pause until close',
      resumeNow: 'Resume now',
      cancel: 'Cancel',
      pausedUntilCloseToast: (label) => `${label} paused until close`,
      pausedForToast: (label, minutes) => `${label} paused for ${minutes} min`,
      resumedToast: (label) => `${label} resumed`,
      minimumOrderTitle: 'Minimum order',
      smallestBasket: 'Smallest basket allowed',
      noMinimum: 'No minimum',
      minimumOrderFooter:
        'The smallest basket total a customer can check out with, per channel. Set to 0 for no minimum.',
      minPrompt: (label) => `Minimum ${label} order total (0 = no minimum)`,
      minDialogTitle: 'Minimum order',
      minSet: (label, amount) => `${label} minimum set to ${amount}`,
      minRemoved: (label) => `${label} minimum removed`,
      deliveryPricingTitle: 'Delivery pricing',
      deliveryPricingFooter:
        'How delivery fees are worked out - by distance from the restaurant, or by drawn map zones.',
      byDistance: 'By distance',
      byDistanceFooter: 'Charge a tiered fee based on how far the customer is.',
      byMapZones: 'By map zones',
      byMapZonesFooter: 'Draw delivery areas on a map, each with its own fee.',
      straightLine: 'Straight-line distance',
      straightLineFooter: 'As the crow flies. Free, instant.',
      drivingDistance: 'Driving distance (MapBox)',
      drivingDistanceFooter: 'Real road distance. More accurate; uses MapBox.',
      deliveryWarnings: {
        title: 'Delivery isn’t ready for customers',
        subtitle: 'Guests will hit these problems at checkout. They won’t be told why.',
        locationNotPinned: 'Your restaurant isn’t pinned on the map',
        locationNotPinnedEffect:
          'Distances are measured from your restaurant, so every address is rejected as out of range.',
        noActiveZones: 'No delivery areas for the pricing method you chose',
        noActiveZonesEffect: 'Every address is told delivery isn’t available there.',
        zoneMissingTime: (zone: string) => `“${zone}” has no delivery time`,
        zoneMissingTimeEffect:
          'Guests get a pickup-style time that leaves out the drive, so orders arrive late.',
        noPaymentMethod: 'No payment method is switched on',
        noPaymentMethodEffect: 'Guests can build a basket but can’t place the order.',
      },
      pinRequiredForRouteTitle: 'Pin your restaurant first',
      pinRequiredForRoute:
        'Driving distance is measured from your restaurant’s location. Set it on the map in restaurant settings, then switch to driving distance.',
      dispatchBufferTitle: 'Dispatch buffer',
      dispatchBufferFooter:
        'Extra minutes added to the driving time — for batching several drops, parking, and getting to the door. Leave at 0 if the route time is already realistic.',
      dispatchBufferLabel: 'Extra minutes',
      restaurantLocation: 'Restaurant location',
      pinned: 'Pinned',
      notSet: 'Not set',
      locationFooter:
        'The point we measure delivery distance and driving time from. Required for distance pricing; on map zones it powers the live delivery ETA (static estimate is used until it is pinned).',
      deliveryPricingSaveFailed: 'Failed to save delivery pricing',
      switchModeConfirmTitle: 'Switch pricing mode?',
      switchModeConfirm:
        'Switching pricing mode - your existing zones for the other method will no longer be used. Continue?',
      deliveryZonesTitle: 'Delivery zones',
      distanceBandsTitle: 'Distance price bands',
      bandForm: {
        newTitle: 'New distance band',
        editTitle: 'Edit distance band',
        back: 'Back',
        nameLabel: 'Band name',
        namePlaceholder: 'e.g. City centre, Up to 5 km',
        kmLabel: 'Delivers within (km)',
        kmPlaceholder: 'e.g. 5',
        feeLabel: 'Delivery fee',
        feePlaceholder: '0',
        minOrderLabel: 'Minimum order',
        minOrderPlaceholder: '0 = no minimum',
        areaLabel: 'Delivery area',
        areaAfter: 'Map-drawn',
        areaFooter: "This area was drawn on a map and can't be edited here.",
        nameRequired: 'Give the band a name.',
        kmRequired: 'Set how far this band delivers (km).',
        invalidInput: 'Values can’t be negative - nothing was saved.',
        invalidNumber: 'Enter a number, e.g. 2.5.',
        wholeMinutes: 'Whole minutes only, e.g. 30.',
        updated: 'Distance band updated',
        added: 'Distance band added',
        deliveryTimeLabel: 'Delivery time (minutes)',
        deliveryTimePlaceholder: 'e.g. 45',
        deliveryTimeInfo: 'Driving time only — kitchen prep time is added on top.',
        deliveryTimeRequired: 'Set how many minutes delivery takes for this area.',
        saveFailed: 'Failed to save band',
      },
      addDistanceBand: '+ Add distance band',
      noDistanceBands:
        'No distance bands yet - delivery is unavailable until at least one band is added.',
      distanceBandsFooter:
        'Charge a flat fee per straight-line distance band (e.g. up to 3 km → €2.50). The nearest band covering the address applies.',
      drivingBandsFooter:
        'Charge a flat fee per driving-distance band (e.g. up to 3 km by road → €2.50). Distance is measured along roads via MapBox; the nearest band covering the address applies.',
      noZones: 'No delivery zones - delivery is unavailable until at least one zone is added.',
      mapArea: 'Map area',
      within: (km) => `Within ${km} km`,
      noRangeSet: 'No range set',
      addZone: '+ Add delivery zone',
      drawArea: '+ Draw a delivery area',
      zonesFooter:
        'Each zone sets how far it reaches and its own delivery fee and minimum order. Customers see the smallest matching zone.',
      deleteZoneConfirm: (label) => `Delete zone "${label}"?`,
      deleteZoneTitle: 'Delete Zone',
      zoneDeleted: 'Zone deleted',
      methodsTitle: 'Manual payment methods',
      noMethods: 'No manual payment methods - only the default options are offered at checkout.',
      active: 'Active',
      inactive: 'Inactive',
      addMethod: '+ Add payment method',
      methodsFooter:
        'Pay-on-arrival or bank-transfer options shown at checkout. Customers follow the instructions and staff confirm receipt manually.',
      deleteMethodConfirm: (name) => `Delete method "${name}"?`,
      deleteMethodTitle: 'Delete',
      methodDeleted: 'Method deleted',
      zoneMapForm: {
        newTitle: 'New delivery area',
        editTitle: 'Edit area',
        drawFooter:
          'Tap the polygon tool (top-left of the map) to start, then tap each corner of your delivery area; double-tap to close the shape. Use the trash tool to redraw. Then set its fee and minimum below.',
        nameLabel: 'Area name',
        namePlaceholder: 'e.g. City centre',
        feeLabel: 'Delivery fee',
        feePlaceholder: '0',
        minOrderLabel: 'Minimum order',
        minOrderPlaceholder: '0 = no minimum',
        deliveryTimeLabel: 'Delivery time (minutes)',
        deliveryTimePlaceholder: 'e.g. 45',
        deliveryTimeInfo: 'Driving time only — kitchen prep time is added on top.',
        nameRequired: 'Give the area a name.',
        drawRequired: 'Draw the delivery area on the map first.',
        minOrderRequired: 'Set the minimum order (enter 0 for no minimum).',
        deliveryTimeRequired: 'Set how many minutes delivery takes for this area.',
        invalidNumber: 'Enter a number, e.g. 2.5.',
        wholeMinutes: 'Whole minutes only, e.g. 30.',
        updated: 'Area updated',
        added: 'Area added',
        saveFailed: 'Failed to save area',
      },
      locationForm: {
        tapPrompt: 'Tap the map to place your restaurant.',
        saved: 'Location saved',
        saveFailed: 'Failed to save location',
        footer:
          "Drag the pin (or tap the map) to your restaurant's exact spot - we measure delivery distance from here.",
        currentCoords: (lat, lng) => ` Current: ${lat}, ${lng}.`,
        clear: 'Clear location',
        cleared: 'Location cleared',
        clearFailed: 'Failed to clear location',
      },
    },
    mapPicker: {
      searchPlaceholder: 'Search an address…',
      search: 'Search',
      noResults: "Couldn't find that - try a more specific name/address, or drop the pin manually.",
      pickHint: 'Pick the closest match, then drag the pin to fine-tune:',
      unavailable: 'Map unavailable - set VITE_MAPBOX_TOKEN to enable location pinning.',
    },
    polygonEditor: {
      unavailable: 'Map unavailable - set VITE_MAPBOX_TOKEN to draw delivery areas.',
      restaurant: 'Restaurant',
      editingArea: 'This area (editing)',
      newArea: 'This area (new)',
    },
  },

  orderCapacity: {
    save: 'Save',
    saving: 'Saving…',
    pickup: 'Pickup',
    delivery: 'Delivery',
    unsavedConfirm: 'You have unsaved changes on this channel. Switch and discard them?',
    unsavedTitle: 'Unsaved changes',
    everyDay: 'Every day',
    dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    dayNamesLong: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    noCap: 'no cap',
    ordersPerHour: (n) => `${n} orders/hour`,
    ordersPerSlotMin: (n, slot) => `${n} orders / ${slot} min`,
    holidayCapText: (max, slot) => `${max} orders/${slot}min`,
    ordersWithUnit: (cap, unit) => `${cap} orders${unit}`,
    defaultCapacity: 'Default capacity',
    defaultCapacityFooter:
      'Slot length, orders per slot, prep time and minimum notice for this channel. Leave the cap blank for no limit.',
    slotIntervalLabel: 'Slot interval (min)',
    slotLengthLabel: 'Slot length (min)',
    ordersPerSlotLabel: 'Orders per slot',
    maxOrdersPerHourLabel: 'Max orders per hour',
    capacityWindowLabel: 'Capacity window (min)',
    capacityWindowFooter:
      'The window the cap is counted over. Leave blank to cap per slot. Set it wider (a multiple of the slot interval) to spread the cap over longer — e.g. slot 15 + window 60 lets guests book every 15 min but caps volume per hour.',
    capacityWindowAlert:
      'Capacity window must be a whole multiple of the slot interval (or blank to cap per slot).',
    prepTimeLabel: 'Kitchen prep time (min)',
    minNoticeLabel: 'Min notice for pre-orders (min)',
    openingBufferLabel: 'Earliest after opening (min)',
    nonePlaceholder: 'none',
    preview: 'Preview',
    defaultFooter:
      'Online ordering follows your opening hours. Add special hours below for different times on specific days.',
    capAlert: 'Cap must be a positive whole number (or blank for no cap)',
    slotAlert: 'Slot length must be a positive whole number',
    prepNoticeAlert: 'Prep / notice must be whole numbers (or blank)',
    openingBufferAlert: (max: number) =>
      `Earliest after opening must be a whole number from 0 to ${max}`,
    saveErrorGeneric: 'Could not save the default capacity - please reload the page and try again.',
    saveFailedTitle: 'Save failed',
    setDefaultFirst: 'Set the default capacity first',
    specialHours: 'Special hours',
    noSpecialHours: 'None - the default applies every day.',
    pausedSuffix: (label) => `${label} · paused`,
    holidayHours: 'Holiday hours',
    holidayFooter:
      'Date-range overrides that replace BOTH special hours and normal hours (open/close + capacity) while active.',
    noHolidays: 'None - normal and special hours apply.',
    offSuffix: (name) => `${name} · off`,
  },

  orders: {
    acceptOrder: 'Accept order',
    markCompleted: 'Mark as completed',
    cancelOrder: 'Cancel order',
    undoCompletion: 'Undo completion',
    restoreOrder: 'Restore order',
    status: {
      PENDING: 'Pending',
      CONFIRMED: 'Confirmed',
      COMPLETED: 'Completed',
      CANCELLED: 'Cancelled',
    },
    type: {
      PICKUP: 'Pickup',
      DELIVERY: 'Delivery',
    },
    itemCount: (count) => `${count} item${count !== 1 ? 's' : ''}`,
    emptyState: {
      noSelection: {
        title: 'No order selected',
        description: 'Select an order from the list to view its details here.',
      },
      notFound: {
        title: 'Order not found',
      },
      loadError: {
        title: 'Failed to load orders',
        description: 'Please try again.',
      },
      noOrders: {
        title: 'No orders',
        filteredDescription: 'No orders match the selected filters.',
        defaultDescription: 'Orders will appear here when customers place them.',
      },
    },
    action: {
      acceptOrder: {
        success: 'Order accepted',
      },
      completeOrder: {
        confirm: 'Mark this order as completed?',
        title: 'Complete order',
        success: 'Order completed',
      },
      cancelOrder: {
        confirm: 'Cancel this order?',
        title: 'Cancel order',
        success: 'Order cancelled',
      },
      undoCompletion: {
        confirm:
          'Undo this completion? The order moves to cancelled, the cash is un-collected and any points it earned are taken back. The guest is not notified.',
        title: 'Undo completion',
        success: 'Completion undone',
      },
      restoreOrder: {
        confirm:
          'Restore this order as completed? The cash is booked as collected again and its points are re-awarded. The guest is not notified.',
        title: 'Restore order',
        success: 'Order restored',
      },
    },
    acceptSheet: {
      title: 'Accept order',
      confirm: 'Confirm & start',
      failed: 'Could not accept the order.',
      prepLabel: 'Prep time',
      deliveryLabel: 'Delivery time',
      minutes: (m) => `${m} min`,
      readyForPickup: 'Ready for pickup',
      foodReady: 'Food ready',
      deliveredBy: 'Delivered by',
      scheduledFor: 'Scheduled for',
      startPrepBy: 'Start prep',
      driverLeaves: 'Driver leaves',
      now: 'Now',
    },
    // Cancel-confirmation sheet — carries the refund choice for a refundable
    // (paid card / bank transfer) order.
    cancelSheet: {
      keep: 'Keep order',
      refundToggle: 'Refund the customer',
      refundLine: (amount: string, provider: string) =>
        `Cancelling will refund ${amount} to the customer via ${provider}.`,
      noRefundWarning: 'Cancel without refunding — the customer will not get their money back.',
      failed: 'Could not cancel the order.',
    },
    payment: {
      acceptProof: {
        confirm: 'Accept this payment proof?',
        title: 'Accept proof',
        success: 'Proof accepted',
      },
      rejectProof: {
        prompt: 'Reason for rejection',
        title: 'Reject proof',
        success: 'Proof rejected',
      },
    },
    paymentStatus: {
      PENDING_COLLECTION: 'Payment pending',
      AWAITING_PROOF: 'Proof pending',
      PROOF_SUBMITTED: 'Proof submitted',
      PROCESSING: 'Processing',
      PAID: 'Paid',
      REJECTED: 'Rejected',
      VOID: 'Void',
      REFUNDED: 'Refunded',
      PARTIALLY_REFUNDED: 'Partially refunded',
      AWAITING_CARD_PAYMENT: 'Card payment pending',
    },
    paymentMethod: {
      CASH: 'Cash',
      MANUAL: 'Bank transfer',
      ONLINE: 'Card',
    },
    refund: {
      reasons: {
        CUSTOMER_REQUEST: 'Customer request',
        ITEM_UNAVAILABLE: 'Item unavailable',
        WRONG_ITEM: 'Wrong item',
        LATE_DELIVERY: 'Late delivery',
        CANCELLED_BY_OWNER: 'Cancelled by owner',
        OTHER: 'Other',
      },
      issued: 'Refund issued',
      // One-tap full refund of a cancelled order.
      now: 'Refund now',
      confirmNow: (amount) => `Refund ${amount} to the customer now?`,
      failed: 'Could not issue the refund.',
      failedReason: (reason) => `Refund failed: ${reason}`,
      recordedPending: 'Refund recorded — settlement pending.',
      info: (amount) => `Up to ${amount} refundable.`,
      amountLabel: 'Amount',
      reasonLabel: 'Reason',
      amountError: 'Enter an amount between 0 and the remaining refundable amount.',
      outOfBandNote:
        'Refunds for this card payment are handled outside the app through your payment provider.',
      button: 'Issue refund',
      submitButton: (amount) => `Refund ${amount}`,
    },
    section: {
      items: 'Items',
      deliveryAddress: 'Delivery address',
      payment: 'Payment',
      refund: 'Refund',
      customerNote: 'Customer note',
      timeline: 'Timeline',
      labels: 'Labels',
    },
    // Operator triage tags on an order (chip row on the detail, compact
    // pills on the list rows). Label NAMES are owner data — never translated.
    fields: {
      status: 'Status',
      kitchen: 'Kitchen',
      subtotal: 'Subtotal',
      deliveryFee: 'Delivery fee',
      discount: 'Discount',
      promoCode: 'Promo code',
      total: 'Total',
      method: 'Method',
      placed: 'Placed',
      scheduledFor: 'Scheduled for',
      estimatedReady: 'Estimated ready',
      prepStarted: 'Prep started',
      refunded: 'Refunded',
      acceptProof: 'Accept proof',
      rejectProof: 'Reject proof',
    },
    kitchen: {
      QUEUED: 'Queued',
      PREPARING: 'Preparing',
      READY: 'Ready',
      SERVED: 'Served',
    },
    quickAction: {
      callCustomer: 'Call customer',
      messageCustomer: 'Message customer',
      call: 'Call',
      message: 'Message',
      printTicket: 'Print bon',
      printTicketAria: 'Print the kitchen ticket for this order',
      printQueued: 'Kitchen ticket queued',
      printQueuedMany: (n: number) => `${n} kitchen tickets queued`,
      printFailed: 'Could not queue the kitchen ticket',
    },
    pause: {
      live: 'Live',
      paused: 'Paused',
      pausedUntilClose: 'Paused · close',
      pausedUntilTime: (time) => `Paused · ${time}`,
      action: {
        label: (label) => `${label} ordering`,
        pause30: 'Pause 30 minutes',
        pause1hour: 'Pause 1 hour',
        pauseHours: (n) => `Pause ${n} hours`,
        pauseUntilClose: 'Pause until close',
        resumeNow: 'Resume now',
      },
      toast: {
        paused: (label) => `${label} paused`,
        pausedFor: (label, minutes) => `${label} paused for ${minutes} minutes`,
        pausedUntilClose: (label) => `${label} paused until close`,
        resumed: (label) => `${label} resumed`,
      },
    },
    summary: {
      total: 'Total',
      pending: 'Pending',
      confirmed: 'Confirmed',
    },
    page: {
      title: 'Orders',
    },
    detail: {
      title: 'Order',
    },
    list: {
      section: {
        pending: 'Pending',
        confirmed: 'Confirmed',
        completed: 'Completed',
        cancelled: 'Cancelled',
      },
    },
    // Operator STAGE section headers on the live orders list (OrderListView).
    stages: {
      NEEDS_REVIEW: 'Payment to review',
      NEEDS_ACCEPTANCE: 'New orders',
      IN_KITCHEN: 'In kitchen',
      READY: 'Ready',
      AWAITING_PAYMENT: 'Awaiting customer payment',
      COMPLETED: 'Completed',
      CANCELLED: 'Cancelled',
    },
    // Live "situation" tiles in the orders navbar (OrderSummaryBar).
    summaryBar: {
      pending: 'Needs action',
      active: 'Active',
      overdue: 'Overdue',
    },
    all: 'All',
    filter: 'Filter',
    now: 'Now',
    copy: 'Copy',
    addressCopied: 'Address copied',
    phoneCopied: 'Phone number copied',
    tel: 'Tel:',
    openInMaps: 'Open in Google Maps',
    mapRoute: 'Route',
    mapDestination: 'Destination',
    deliveryDistance: (distance: string) => `${distance} from the restaurant`,
    expired: 'Expired',
    autoCancelsIn: 'Auto-cancels in',
    pickupLocation: 'Pickup location',
    paymentProof: 'Payment proof',
    // How the matched delivery zone computed the fee - shown under "Delivery fee".
    zoneMethod: {
      STRAIGHT_LINE: 'straight-line distance',
      ROUTE_DISTANCE: 'driving distance (MapBox)',
      POLYGON: 'map zone',
    },
    zoneLabel: (label, method) => `Zone: ${label} · ${method}`,
    ordersCount: (n) => `${n} order${n !== 1 ? 's' : ''}`,
    ordersByHour: (n) => `${n} order${n !== 1 ? 's' : ''} by hour`,
    ordersByHourLabel: 'Orders by hour',
    chartBusiest: (label, count) => `. Busiest ${label} · ${count}`,
    capacityPerHour: (cap, label) => `${cap}/h ${label}`,
    nextTwoHours: (n) => `Next 2h · ${n} order${n !== 1 ? 's' : ''}`,
    sectionCount: (label, count) => `${label} · ${count}`,
    // Customer-uploaded bank-transfer proof (OrderInspector).
    proof: {
      openPdf: 'Open uploaded PDF proof',
      alt: 'Customer payment proof',
      openFullSize: 'Open full size',
      customerStated: (amount) => `Customer stated: ${amount}`,
      uploaded: (when) => `Uploaded ${when}`,
      rejectHint: 'Order stays open - the customer can re-upload until the payment deadline.',
      rejectSuccess: 'Proof rejected - order stays open for re-upload',
    },
    // Payment status wording in the inspector (operator language; the special
    // cases below override the base map for certain method/status pairs).
    paymentStatusLabels: {
      PENDING_COLLECTION: 'Pending collection',
      AWAITING_PROOF: 'Awaiting proof',
      PROOF_SUBMITTED: 'Proof submitted',
      PROCESSING: 'Processing',
      ON_HOLD: 'Under review',
      PAID: 'Paid',
      REJECTED: 'Rejected',
      VOID: 'Void',
      REFUNDED: 'Refunded',
      PARTIALLY_REFUNDED: 'Partially refunded',
      awaitingCardPayment: 'Awaiting card payment',
      underReviewByProvider: 'Under review by provider',
      awaitingCardCollection: 'Awaiting card collection',
    },
    // Operator-facing payment-rail names (payment-method util).
    methodKind: {
      CASH: 'Cash',
      CARD_ON_HANDOFF: 'Card on handoff',
      MANUAL: 'Bank transfer',
      ONLINE: 'Card',
      GIFT_CARD: 'Gift card',
    },
    // Compact payment pill on the order card (payment-pill util).
    pill: {
      paid: 'Paid',
      refunded: 'Refunded',
      awaiting: 'Awaiting payment',
      failed: 'Payment failed',
      refundNeeded: 'Refund needed',
    },
    // Refund panel extras not covered by `refund` above.
    refundExtra: {
      max: (amount, currency) => `Max ${amount} (${currency})`,
      confirmSettled: 'Confirm settled',
      settled: '✓ Settled',
      failed: 'Refund failed',
      settlePrompt: 'Confirm you have paid this refund to the customer outside the app?',
      settleTitle: 'Confirm refund settled',
      settledToast: 'Refund settled',
      pending: (amount) => `+${amount} pending`,
      pendingShort: 'Refund pending',
    },
    // Online-ordering health strip above the list (OrderPauseCard).
    channelStatus: {
      open: 'Open',
      closed: 'Closed',
      paused: 'Paused',
      close: 'close',
    },
    // Relative order timing shown on the card + inspector hero (time util).
    timing: {
      asap: 'ASAP',
      labelScheduled: 'Scheduled',
      labelReadyBy: 'Ready by',
      labelDeliveredBy: 'Delivered by',
      labelPlaced: 'Placed',
      dueNow: 'due now',
      inMin: (m) => `in ${m} min`,
      inHrMin: (h, m) => (m ? `in ${h}h ${m}m` : `in ${h}h`),
      minOverdue: (m) => `${m} min overdue`,
      hrMinOverdue: (h, m) => (m ? `${h}h ${m}m overdue` : `${h}h overdue`),
      placedAgo: (ago) => `placed ${ago}`,
      justNow: 'just now',
      minAgo: (m) => `${m} min ago`,
      hrMinAgo: (h, m) => (m ? `${h}h ${m}m ago` : `${h}h ago`),
      chipScheduled: 'scheduled',
      chipReady: 'ready',
      chipOverdue: 'overdue',
      shortNow: 'now',
      shortMin: (m) => `${m}m`,
      shortHr: (h) => `${h}h`,
    },
    awaitingPayment: {
      title: 'Waiting for guest to pay',
      tooltip: 'Waiting for guest to pay',
      refresh: 'Refresh',
      loadError: "Couldn't load orders awaiting payment.",
      empty: 'No orders waiting for the guest to pay',
      emptyDescription:
        'Orders waiting for a bank transfer proof or an online payment show up here.',
      footer:
        'These orders are not in the kitchen queue yet. They move to Orders once payment is confirmed and are cancelled automatically when the deadline passes.',
      expiresIn: 'Payment deadline:',
      noDeadline: 'no deadline',
      call: (name: string) => `Call ${name}`,
    },
    // Order History page (OrderHistoryPage).
    history: {
      title: 'Order History',
      tooltip: 'Order history',
      refresh: 'Refresh',
      loading: 'Loading orders…',
      loadError: "Couldn't load history.",
      tryAgain: 'Try again',
      empty: 'No orders in this range.',
      today: 'Today',
      yesterday: 'Yesterday',
      filters: 'Filters',
      reset: 'Reset',
      showOrders: 'Show orders',
      prevMonth: 'Previous month',
      nextMonth: 'Next month',
      weekdayInitials: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
      tabs: {
        all: 'All',
        pending: 'Pending',
        active: 'Active',
        done: 'Done',
        void: 'Void',
      },
      statusLabel: {
        PENDING: 'Pending',
        CONFIRMED: 'In progress',
        COMPLETED: 'Completed',
        CANCELLED: 'Cancelled',
      },
    },
  },

  print: {
    // Server Direct Print (shared: Printers list + Printer add/edit form)
    serverDirectTitle: 'Server Direct Print',
    serverDirectFooter:
      'Enter these two URLs in each Epson’s Server Direct Print setup, along with that printer’s own Printer ID (shown when you open the printer).',
    printUrlLabel: 'Print URL',
    notificationUrlLabel: 'Notification URL',
    printerIdLabel: 'Printer ID',
    copyButton: 'Copy',
    copied: (label) => `${label} copied`,
    okButton: 'OK',

    // Printer status (shared: printer badges + status-history events)
    statusOnline: 'Online',
    statusOffline: 'Offline',
    statusError: 'Error',
    defaultBadge: 'Default',

    // Printers list page
    couldntLoadPrinters: 'Couldn’t load printers',
    noPrintersTitle: 'No printers yet',
    noPrintersDesc: 'Add the kitchen printer so fired orders print. Tap + to get started.',
    printersTitle: 'Printers',
    printersFooter:
      'Status updates as each printer checks in. A printer that hasn’t checked in recently shows Offline; queued tickets wait and print when it’s back.',
    queued: (n) => `${n} queued`,
    noJobsQueued: 'No jobs queued',
    routingAndHistoryTitle: 'Routing & history',
    routingTitle: 'Ticket routing',
    historyTitle: 'Print history',
    behaviourTitle: 'What to print',

    // What-to-print settings page
    behaviourPageTitle: 'What to print',
    behaviourPageFooter:
      'Choose which confirmations print a ticket automatically. Tickets go to the default printer.',
    printOnlineOrdersLabel: 'Online orders',
    printOnlineOrdersFooter:
      'Print a kitchen ticket the moment you accept a pickup or delivery order.',
    printReservationsLabel: 'Confirmed reservations',
    printReservationsFooter:
      'Print a front-of-house ticket when you confirm a reservation. Off by default.',
    onlineOrdersUpdated: 'Online-order printing updated',
    reservationsUpdated: 'Reservation printing updated',

    // Ticket text size
    textSizeTitle: 'Ticket text',
    textSizeLabel: 'Text size',
    textSizeMedium: 'Medium',
    textSizeLarge: 'Large',
    textSizeFooter:
      'Large text is easier to read across the kitchen, but fits less on a line and uses about twice the paper. Use Test print on a printer to see it.',
    textSizeUpdated: 'Ticket text size updated',
    itemCategoryLabel: 'Show category under each item',
    itemCategoryFooter:
      'Prints the menu category in brackets below every item, like [Drinks]. Helpful when one printer serves several stations; turn it off for a shorter ticket.',
    itemCategoryUpdated: 'Category line updated',
    itemLabelTitle: 'Item labels',
    itemLabelName: 'Name only',
    itemLabelCodeAndName: 'Code and name',
    itemLabelCodeOnly: 'Code only',
    itemLabelFooter:
      'Menu codes keep a ticket short, which matters most at large text. A dish with no code always prints its name, whichever option you pick.',
    itemLabelUpdated: 'Item labels updated',
    dateTimeFormatLabel: 'Date format',
    dateTimeFormatFooter:
      'How every ticket prints the date. Times always print in 24-hour form, like 19:30.',
    dateTimeFormatUpdated: 'Date format updated',
    // Ticket preview
    previewTitle: 'Ticket preview',
    previewFooter:
      'A sample ticket rendered with the settings above — what your printer will produce. Text size, item categories and item labels update it live.',
    previewError: 'Could not render the preview.',

    // Printer add/edit form
    editPrinterTitle: 'Edit printer',
    addPrinterTitle: 'Add printer',
    printerGone: 'This printer no longer exists.',
    nameLabel: 'Name',
    namePlaceholder: 'e.g. Kitchen, Bar, Receipt',
    activeLabel: 'Active',
    defaultPrinterLabel: 'Default printer',
    defaultPrinterFooter:
      'Tickets for menu categories with no specific printer - and every online order - print to the default printer.',
    migratingTitle: 'Migrating a printer?',
    migratingFooter:
      'Already set up an Epson on the old system? Paste its Printer ID here to keep it printing with no re-pairing. Leave blank and we’ll generate a new one.',
    existingIdLabel: 'Existing Printer ID',
    existingIdPlaceholder: 'Leave blank to generate a new one',
    printerSetupTitle: 'Printer setup',
    printerSetupFooter:
      'Open the printer’s Server Direct Print setup and enter the Print URL, Notification URL and this Printer ID so it pairs with In-House.',
    // Test print
    testPrintButton: 'Print a test ticket',
    testPrintSending: 'Sending…',
    testPrintFooter:
      'Sends a test ticket to this printer. It prints within a few seconds of the printer next checking in.',
    testPrintQueued: 'Test ticket sent — watch the printer',
    testPrintFailed: 'Could not send the test ticket.',

    statusHistoryTitle: 'Status history',
    eventConnected: 'Connected',
    noStatusEvents: 'No status changes recorded yet',
    noStatusEventsFooter: 'Connectivity and errors appear here as they happen.',
    nameRequired: 'Give the printer a name.',
    saveFailed: 'Could not save.',
    idFormatInvalid: 'Printer ID must be 8–16 letters and numbers.',
    printerUpdated: 'Printer updated',
    printerAdded: 'Printer added',

    // Ticket routing page (stations / pages)
    routingNoPrintersDesc: 'Add a printer first, then set up where each order prints.',
    noneSet: 'None set',
    noMenuCategories: 'No menu categories yet.',
    mainPrinterLabel: 'Main printer',
    mainPrinterFooter:
      'The whole order prints here - and any items not assigned to a station below also print here.',
    splitTitle: 'Split by station',
    splitToggleLabel: 'Split kitchen tickets',
    splitFooter:
      'On: each order is split across station printers by the stations below. Off: the whole order prints as one ticket on the main printer.',
    splitUpdated: 'Ticket splitting updated',
    stationsTitle: 'Stations',
    stationsFooter:
      'Each station prints the menu categories you assign it. Categories in no station print to the main printer.',
    addStationLabel: 'Add station',
    stationCategoriesCount: (n) => (n === 1 ? '1 category' : `${n} categories`),
    stationSelectionSummary: (categories: number, products: number) => {
      const cat = categories === 1 ? '1 category' : `${categories} categories`;
      if (products === 0) return cat;
      const prod = products === 1 ? '1 product' : `${products} products`;
      return `${cat} · ${prod}`;
    },
    expoTitle: 'Expo copy',
    expoToggleLabel: 'Also print a full ticket to the main printer',
    expoFooter:
      'Prints one ticket with every item to the main printer - the expo/pass copy. Stations still get their own tickets.',
    expoUpdated: 'Expo copy updated',

    // Station (page) edit form
    editStationTitle: 'Edit station',
    addStationTitle: 'Add station',
    stationNameLabel: 'Name',
    stationNamePlaceholder: 'e.g. Hot Kitchen, Bar, Grill',
    stationPrinterTitle: 'Printer',
    noActivePrinters: 'No active printers. Add or activate one first.',
    stationCategoriesTitle: 'Menu categories',
    stationCategoriesFooter: 'Tick the categories this station prints.',
    stationMenuTitle: 'Menu — tap a category to expand',
    stationMenuFooter:
      'Tick a category to route it whole. Expand and tick individual products to carve them to this station instead.',
    stationProductsTitle: 'Specific products',
    stationProductsFooter:
      'Products ticked here override their category — use it when one category mixes stations.',
    noMenuProducts: 'No menu products yet.',
    claimedByStation: (station: string) => `On ${station}`,
    viaCategory: 'via category',
    legendAll: 'Whole category prints on this station',
    legendPartial: 'Partial — some products carved to another station',
    legendOther: 'Another station owns this item',
    stationDeleteButton: 'Delete station',
    stationGone: 'This station no longer exists.',
    stationSaved: 'Station saved',
    stationDeleted: 'Station deleted',
    stationNameRequired: 'Give the station a name.',
    stationPrinterRequired: 'Choose a printer.',

    // Print history (jobs) page
    jobStatusAll: 'All',
    jobStatusQueued: 'Queued',
    jobStatusPrinting: 'Printing',
    jobStatusPrinted: 'Printed',
    jobStatusFailed: 'Failed',
    couldntLoadJobs: 'Couldn’t load jobs',
    noJobsTitle: 'No print jobs',
    noJobsDesc: 'Tickets show here as orders are fired.',
    receiptLabel: 'Receipt',
    kitchenTicketLabel: 'Kitchen ticket',
    reprintButton: 'Reprint',
    reprintQueued: 'Reprint queued',
    copyContent: 'Copy print content',
    contentCopied: 'Print content copied',
    reprintFooter:
      'Tap a ticket to reprint it - a fresh job is queued; the original stays in history.',
  },

  promotions: {
    title: 'Promotions',
    newPromotion: 'New promotion',
    editPromotion: 'Edit promotion',
    details: 'Details',
    name: 'Name',
    namePlaceholder: 'Weekend 10% off',
    description: 'Description',
    descriptionPlaceholder: 'Shown to customers on the storefront',
    discount: 'Discount',
    discountType: {
      percentage: 'Percentage off',
      fixedAmount: 'Fixed amount off',
      freeDelivery: 'Free delivery',
    },
    percentOff: 'Percentage off',
    percentOffHint: 'e.g. 10 for 10% off',
    maxDiscount: (currency) => `Maximum discount (${currency}, optional)`,
    maxDiscountPlaceholder: 'No cap',
    amountOff: (currency) => `Discount amount (${currency})`,
    amountOffPlaceholder: '5.00',
    appliesto: 'Applies to',
    scope: {
      wholeOrder: 'Whole order',
      selectedItems: 'Selected items',
    },
    conditions: 'Conditions',
    orderType: {
      dineIn: 'Dine in',
      pickup: 'Pickup',
      delivery: 'Delivery',
    },
    paymentMethodsTitle: 'Payment methods',
    paymentMethodsFooter: 'None selected = applies to every payment method',
    paymentMethod: {
      cash: 'Cash',
      cardOnHandoff: 'Card on pickup/delivery',
      online: 'Online payment',
      manual: 'Bank transfer',
    },
    minimumOrder: (currency) => `Minimum order (${currency}, optional)`,
    minimumOrderPlaceholder: 'No minimum',
    dateWindow: 'Date window',
    starts: 'Starts',
    startDate: 'Start date',
    ends: 'Ends',
    endDate: 'End date',
    howUnlocked: 'How it is unlocked',
    trigger: {
      automatic: 'Automatic (no code)',
      codeRequired: 'Requires a code',
    },
    codeType: {
      shared: 'Shared code (one code, everyone)',
      personal: 'Personal coupons (issued per customer)',
    },
    code: 'Code',
    codePlaceholder: 'SAVE10',
    maxRedemptions: 'Maximum redemptions (optional)',
    maxRedemptionsPlaceholder: 'Unlimited',
    oncePerCustomer: 'Once per customer',
    visibilityStacking: 'Visibility & stacking',
    showOnStorefront: 'Show on storefront',
    showOnStorefrontFooter: 'Banner + product badges for guests',
    stackable: 'Stackable',
    stackableFooter: 'Can be combined with other stackable promotions',
    priority: 'Priority',
    priorityHint: 'Lower is applied first',
    savePromotion: 'Save promotion',
    giveNameError: 'Give the promotion a name.',
    pickOrderTypeError: 'Select at least one order type.',
    chooseDiscountError: 'Choose what the discount applies to.',
    saved: 'Promotion saved',
    issuePersonalCoupon: 'Issue personal coupon',
    customer: 'Customer',
    customerSearchPlaceholder: 'Search by name or phone',
    change: 'Change',
    couponCode: 'Coupon code',
    couponCodePlaceholder: 'VIP-JANE',
    sharable: 'Sharable',
    sharableFooter: 'Anyone with the code can redeem it, not just this customer',
    issuing: 'Issuing…',
    issueCoupon: 'Issue coupon',
    pickCustomerError: 'Select a customer first.',
    enterCouponError: 'Enter a coupon code.',
    couponIssuedTo: (customerName) => `Coupon issued to ${customerName}`,
    active: 'Active',
    all: 'All',
    couldNotLoadError: 'Could not load promotions',
    noneRunning: 'Nothing running right now',
    noneRunningDescription: 'Expired and upcoming offers are under All.',
    noneYet: 'No promotions yet',
    noneYetDescription:
      'Create a discount - a percentage or fixed amount off, unlocked automatically or by a code. Tap +.',
    codeDisplay: (code) => `Code: ${code}`,
    archivedBadge: 'Archived',
    scheduledBadge: 'Scheduled',
    expiredBadge: 'Expired',
    codeBadge: 'Code',
    archive: 'Archive',
    archiveConfirm: (name) =>
      `Archive "${name}"? It is deactivated immediately; redemption history is kept.`,
    archiveTitle: 'Archive promotion',
    archived: 'Promotion archived',
    howWorks: 'How promotions work',
    howWorksDescription:
      'Automatic promotions apply at checkout without a code. Code promotions need a shared code (for everyone) or a personal coupon you issue for a customer. Discounts show on the storefront and reduce the order total.',
    // Compact labels on the promotions list rows.
    summaryPercent: (pct) => `${pct}% off`,
    summaryAmount: (amount) => `${amount} off`,
    summaryWholeOrder: 'whole order',
    summaryCategories: (n) => `${n} categor${n === 1 ? 'y' : 'ies'}`,
    summaryProducts: (n) => `${n} product${n === 1 ? '' : 's'}`,
    summaryCashOnly: 'Cash only',
    // Edit-page states & extra validation.
    endBeforeStartError: 'The end date can’t be before the start date.',
    scheduleWindowError: 'Check the day & hour windows - each end time must be after its start.',
    loadingMenu: 'Loading menu…',
    menuLoadError: 'Couldn’t load the menu. Pull to retry.',
    noCategoriesYet: 'No categories yet - add them under Menu first.',
    noProductsYet: 'No products yet - add them under Menu first.',
    // Weekly day/time schedule.
    daysAndHours: 'Days & hours',
    limitToDays: 'Limit to certain days & times',
    limitToDaysFooter: 'Off = applies any day within the date window above',
    windowLabel: (n) => `Window ${n}`,
    hours: 'Hours',
    addWindow: 'Add window',
    startAria: (day) => `${day} start`,
    endAria: (day) => `${day} end`,
    removeWindow: 'Remove window',
    customerFallback: 'Customer',
    // Issued-coupon history (discount detail) + a customer's coupon wallet.
    issuedCoupons: 'Issued coupons',
    noCouponsIssued: 'No coupons issued yet',
    customerCoupons: 'Private coupons',
    noCustomerCoupons: 'No private coupons yet',
    couponUnused: 'Unused',
    couponRedeemed: 'Redeemed',
    couponIssuedOn: (date) => `Issued ${date}`,
    couponRedeemedOn: (date) => `Used ${date}`,
    loadingCoupons: 'Loading coupons…',
    couldNotLoadCoupons: 'Could not load coupons',
    // Simple / Advanced editor mode.
    simpleMode: 'Simple',
    advancedMode: 'Advanced',
    advancedValuesHint:
      'This discount uses advanced options - switch to Advanced to see and edit them.',
    // Select-field labels.
    discountTypeLabel: 'Type',
    triggerLabel: 'Trigger',
    codeTypeLabel: 'Code type',
  },

  reports: {
    overview: {
      netSales: 'Net sales',
      onlineOrders: 'Online orders',
      dineInTabs: 'Dine-in tabs',
      tips: 'Tips',
      refunds: 'Refunds',
      discounts: 'Discounts',
      vsPrevious: (baseline) => `vs ${baseline}`,
      couldntLoad: 'Could not load reports',
      noSales: 'No sales in this period',
      noSalesHint: 'Pick a different period, or come back once orders start coming in.',
      seeAllItems: 'See all',
      detailedReports: 'Detailed reports',
      tabSales: 'Sales',
      tabOperations: 'Operations',
      trendNeedsMoreDays: 'Pick a longer period to see a trend.',
      byHour: 'By hour',
      tablesUsed: 'Tables used',
      turnsPerTable: (turns) => `${turns} sittings per table`,
      avgDwell: 'Avg. stay',
      minutes: (count) => `${count} min`,
      busiestTables: 'Busiest tables',
      turns: (count) => `${count} sittings`,
    },
    periodSelector: {
      current: 'Period',
      compare: 'Compared with',
      basis: {
        previousPeriod: 'Period before',
        samePeriodLastYear: 'Last year',
      },
    },
    salesOverview: {
      title: 'Sales overview',
      netSales: 'Net sales',
      gross: 'Gross sales',
      covers: 'Covers',
      avgTab: 'Avg. tab',
      revenueTrend: 'Revenue trend',
      byChannel: 'By channel',
      online: 'Online',
      dineIn: 'Dine in',
      byTimeOfDay: 'By time of day',
    },
    dayParts: {
      breakfast: 'Breakfast',
      lunch: 'Lunch',
      afternoon: 'Afternoon',
      dinner: 'Dinner',
      late: 'Late',
    },
    dayEnd: {
      title: 'Day end',
      day: 'Day',
      couldntLoad: 'Could not load day end',
      netReceived: 'Net received',
      takings: 'Takings',
      refunds: 'Refunds',
      serviceCharge: 'Service charge',
      tipsPayout: 'Tips (payout)',
      vat: 'VAT',
      openTabsWarning: (count, plural) =>
        `${count} ${plural} still open - these are takings so far, not a final figure.`,
      tabPlural: 'tab is',
      tabsPluralForm: 'tabs are',
      reconciliation: 'From net sales to takings',
      netSales: 'Net sales',
      deliveryFees: 'Delivery fees',
      refundsAddedBack: 'Refunds (already deducted above)',
      otherDifference: 'Settled on another day',
      byPaymentMethod: 'By payment method',
      cashUp: 'Cash up',
      expectedInDrawer: 'Expected in drawer',
      counted: 'Counted',
      countedPlaceholder: 'Enter the counted amount',
      balanced: 'Balanced',
      over: 'Over',
      short: 'Short',
    },
    paymentReconciliation: {
      title: 'Payment reconciliation',
      couldntLoad: 'Could not load report',
      paymentCount: (count, plural) => `${count} ${plural}`,
      paymentSingular: 'payment',
      paymentPlural: 'payments',
      grossTaken: 'Gross taken',
      refunds: 'Refunds',
      net: 'Net',
      tips: 'Tips',
    },
    topItems: {
      title: 'Top sellers',
      byQuantity: 'By quantity',
      byRevenue: 'By revenue',
      couldntLoad: 'Could not load report',
      noSalesYet: 'No sales yet',
      noItemsSoldInRange: 'No items sold in this period.',
      sold: (quantity) => `${quantity} sold`,
    },
    rangePresets: {
      today: 'Today',
      yesterday: 'Yesterday',
      thisWeek: 'This week',
      last7: 'Last 7',
      thisMonth: 'This month',
    },
  },

  reservationSetup: {
    title: 'Reservation Setup',
    bookings: 'Bookings',
    bookingsFooter:
      'Turn online booking on or off, auto-confirm requests, or pause for the rest of today.',
    acceptingReservations: 'Accepting reservations',
    on: 'On',
    off: 'Off',
    stopAcceptingOnline:
      'Stop accepting online reservations? The booking page will be hidden from guests and new requests will be declined until you turn it back on.',
    turnReservationsOff: 'Turn reservations off',
    startAcceptingOnline: 'Start accepting online reservations again?',
    turnReservationsOn: 'Turn reservations on',
    reservationsTurnedOff: 'Reservations turned off',
    reservationsTurnedOn: 'Reservations turned on',
    confirmation: 'Confirmation',
    howBookingsConfirmed: 'How bookings are confirmed',
    automaticConfirm: 'Automatic - confirm instantly',
    bookingsAutoConfirmed: 'Bookings confirmed automatically',
    manualConfirm: 'Manual - staff review each request',
    bookingsManualReview: 'Bookings require manual review',
    confirmationAutomatic: 'Automatic',
    confirmationManual: 'Manual review',
    confirmationFooter:
      'When off, the booking page is hidden from guests and new online reservations are declined. With automatic confirmation a request is booked instantly; manual review keeps it pending until staff accept it.',
    partySize: 'Party size',
    partySizeFooter:
      'The largest party guests can book online, and the message shown when they go over it.',
    largestParty: 'Largest party',
    maxGuestsPerBooking: 'Maximum number of guests per booking',
    partySizeUpdated: 'Party size updated',
    guestsCount: (count) => `${count} guests`,
    largePartyMessage: 'Large party message',
    largePartyMessageHidden: 'Hidden',
    shownToGuestsAboveMax: 'Shown to guests whose party is above the maximum (leave empty to hide)',
    messageUpdated: 'Message updated',
    partySizeRange:
      'The range of party sizes guests can select when booking online. Larger parties see a large-party message instead of a reservation form.',
    bookingWindow: 'Booking window',
    bookingWindowFooter:
      'How far ahead guests can book, the last-minute cutoff, and the spacing between bookable times.',
    guestsCanBookUpTo: 'Guests can book up to',
    bookingHorizon: 'Booking horizon',
    daysAheadGuests: 'How many days ahead guests can book',
    bookingWindowUpdated: 'Booking window updated',
    daysAhead: (count) => `${count} days ahead`,
    lastMinuteCutoff: 'Last-minute cutoff',
    hoursBeforeSlot: 'Hours before the slot to stop bookings (0 = until start)',
    cutoffUpdated: 'Cutoff updated',
    untilStart: 'Until start',
    hoursBefore: (hours) => `${hours}h before`,
    slotSpacing: 'Slot spacing',
    minutesBetweenBookableTimes: 'Minutes between bookable times',
    slotSpacingUpdated: 'Slot spacing updated',
    lastBookingBeforeClose: 'Last booking before closing',
    stopBookingsBeforeClose: 'Stop taking bookings before closing',
    upToClose: 'Up to closing',
    lastBookingBeforeCloseUpdated: 'Last booking before closing updated',
    everyMinutes: (minutes) => `Every ${minutes} minutes`,
    bookingWindowControls:
      'Controls how far the calendar opens, how close to service guests can still book, and the spacing of the times they can choose.',
    tableTiming: 'Table timing',
    tableTimingFooter: 'How long a table is held per booking, so it frees up for the next guest.',
    defaultTableTime: 'Default table time',
    tableHeldPerBooking: 'How long a table is held per reservation, in minutes (minimum 15)',
    tableTimeUpdated: 'Table time updated',
    tableTimingHint:
      'How long each reservation holds a table by default. A per-shift override is under Capacity; the turnaround buffer between parties is set there too (advanced mode).',
    noShows: 'No-shows',
    noShowsFooter: 'How long after the booking time an unarrived guest is counted as a no-show.',
    noShowGracePeriod: 'No-show grace period',
    minutesPastBooking: 'Minutes past the reservation time before a guest counts as a no-show',
    gracePeriodUpdated: 'Grace period updated',
    noShowsHint:
      'The grace period drives no-show alerts - staff are notified once a reservation is overdue by this many minutes.',
    enterWholeNumber: (min) => `Enter a whole number of ${min} or more`,
    enterWholeNumberBetween: (min, max) => `Enter a whole number from ${min} to ${max}`,
    saveFailed: 'Save failed',
    invalidValue: 'Invalid value',
    couldntLoadSettings: 'Could not load reservation settings',
    checkConnection: 'Check your connection and try again.',
    capacity: 'Capacity',
    capacityMode: 'Capacity mode',
    capacityModeFooter:
      'Choose how tables and covers are managed - one shared limit, or per-zone tables with pacing.',
    simpleMode: 'Simple',
    simpleModeDesc: 'Per-slot caps for reservations / covers. No per-table assignment.',
    simpleModeConfirm: 'Switch capacity to simple mode?',
    simpleModeDetails:
      'A slot is a 60-minute reservation window - e.g. the 7:00 PM slot counts every reservation from 7:00 PM to 7:59 PM.\n\nThe default caps apply whenever no time-specific shift covers a reservation. Without a default, reservations are accepted only within time-specific shifts.',
    advancedMode: 'Advanced',
    advancedModeDesc: 'Pacing caps (covers / parties per 15 minutes) and per-table assignment.',
    advancedModeConfirm: 'Switch capacity to advanced mode?',
    advancedModeDetails:
      'Pacing caps apply per rolling 15-minute window - finer-grained than the simple slot model and better suited to busy services.\n\nEach reservation is also assigned to a specific table from the floor plan, so capacity is limited by both pacing limits and physical seats.',
    capacityModeColon: (mode) => `Capacity mode: ${mode}`,
    failedSwitchMode: 'Failed to switch capacity mode',
    // Capacity page - mode tile feature bullets
    simpleFeature1: 'Per-slot caps on reservations & covers',
    simpleFeature2: 'One 60-min booking window per slot',
    simpleFeature3: 'No per-table assignment',
    advancedFeature1: 'Pacing caps per rolling 15 min',
    advancedFeature2: 'Concurrent (active-at-once) caps',
    advancedFeature3: 'Per-table assignment from the floor plan',
    // Capacity page - load error state
    couldntLoadCapacity: 'Couldn’t load capacity settings',
    serverNoResponse: 'The server didn’t respond. Please try again.',
    retrying: 'Retrying…',
    tryAgain: 'Try again',
    // Capacity page - inline labels / placeholders / footers (noLimitation reused from below)
    capacityAndLimit: 'Capacity & limit',
    pacingTitle: 'Pacing',
    concurrentTitle: 'Concurrent',
    concurrentFooter: 'Leave blank for no limitation. Usually set below your table count.',
    unassignedZone: 'Unassigned',
    aboutSection: (title) => `About ${title}`,
    topGroup: (count, seats) => `${count}× ${seats}-top`,
    tblShortSeats: (count, seats) => `${count} tbl · ${seats} seats`,
    noBookableTablesPrefix: 'No bookable tables configured yet.',
    noBookableTablesSuffix: 'to plan ADVANCED capacity.',
    // Capacity page - shift list formatters
    everyDay: 'Every day',
    dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    dayNamesLong: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    coversPer15: (n) => `${n} covers/15min`,
    partiesPer15: (n) => `${n} parties/15min`,
    windowSubtitle: (start, end, days) => `${start}–${end} · ${days}`,
    // Capacity page - preview timeline tooltips (resSlot/coversSlot/pacingCovers reused from below)
    capResCoversPerSlot: (res, covers) => `${res} reservations / ${covers} covers per slot`,
    tablesAvailable: 'Tables available',
    manage: 'Manage',
    noBookableTables: 'No bookable tables configured yet. Add tables to plan ADVANCED capacity.',
    addTables: 'Add tables',
    tblSeats: (count, seats) => `${count} tables · ${seats} seats`,
    bookableTablesInfo: (tables, seats, zones, plural) =>
      `${tables} bookable tables · ${seats} seats across ${zones} zone${plural}. ADVANCED capacity assigns each reservation to a specific table, so seating capacity is limited by what's on the floor.`,
    tableturnaround: 'Table turnaround',
    tableturnaroundInfo:
      "Minutes a table stays blocked after a reservation ends before the next party can be seated there - cleaning, resetting, settling up.\n\nApplied on top of each reservation's duration when assigning tables. Used only in advanced mode (simple mode counts slots, not table occupancy).",
    bufferMin: 'Buffer (min)',
    bufferUpdated: 'Buffer updated',
    bufferMustBePositive: 'The buffer must be 0 or more minutes',
    bufferInfo:
      'Gap between two parties at the same table. 0 = back-to-back. Applies to every advanced shift.',
    defaultPacing: 'Default pacing (per 15 minutes)',
    pacingInfo:
      'Pacing limits the arrival rate - how many new reservations can START in any rolling 15-minute window.\n\n"Max covers" limits total guests; "max parties" limits total reservations. Two parties 20 minutes apart never hit the same pacing window, even if they overlap on the floor - use the concurrency limits below for that.\n\nProtects the moment guests come in: kitchen ramp-up, greeting / seating throughput.',
    maxCovers: 'Max covers',
    maxParties: 'Max parties',
    defaultConcurrent: 'Default concurrency (active at once)',
    concurrentInfo:
      'Concurrency limits cap how many reservations can be ACTIVE at any moment - those whose [start, start + duration) interval covers the requested time.\n\nUseful as a soft limit below physical seats: e.g. you have 12 tables but only enough staff to comfortably manage 8 parties at once.\n\nIndependent of pacing: two parties an hour apart never share a pacing window but can still both be seated.',
    leaveBlankNoLimitation: 'Leave blank for no limit.',
    shiftOverrideWarning:
      'Time-specific shifts below OVERRIDE these default limits during their hours - set a limit on each shift too, or it won’t apply.',
    detailOverrideWarning:
      'This shift OVERRIDES the default limits during its hours. Set its own caps here.',
    preview: 'Preview',
    defaultCoverage: 'Default · all day',
    maxReservationsSlot: 'Max reservations / slot',
    capsLimit:
      "Caps the number of reservations (parties) that can start in the same 60-minute slot - protects operational load, regardless of each party's size. Leave blank for no limit.",
    maxCoversSlot: 'Max covers / slot',
    coversCapLimit:
      'Caps the total guests starting in the same slot - the sum of all party sizes. Protects seating capacity. Leave blank for no limit.',
    clearDefault: 'Clear default',
    removeDefaultCapacity:
      'Remove the default capacity? Reservations outside a time-specific shift will be declined.',
    timeSpecificShifts: 'Time-specific shifts',
    noTimeSpecificShifts:
      'No time-specific shifts. Add one to tighten capacity (or open booking hours for a specific window).',
    addAShift: 'Add a shift',
    newTimeSpecificShift: 'New time-specific shift',
    name: 'Name',
    lunchDinner: 'Lunch / Dinner …',
    startTime: 'Start time',
    endTime: 'End time',
    shiftFooter:
      'Active on every day of the week, with limits inherited from the default. Tap the new shift to refine days and tighten its limits.',
    shift: 'Shift',
    shiftNotFound: 'Shift not found.',
    baselineEditedInline: 'This shift is the baseline. Edit it from the Capacity page.',
    timeSpecificShiftTitle: 'Time-specific shift',
    daysActive: 'Active days',
    capsSimple: 'Limits (Simple)',
    maxReservationsSlotLabel: 'Max reservations / slot',
    maxReservationsInfo:
      "Caps the number of reservations (parties) that can start in the same 60-minute slot - protects operational load, regardless of each party's size.",
    maxCoversSlotLabel: 'Max covers / slot',
    maxCoversInfo:
      'Caps the total guests starting in the same slot - the sum of all party sizes. Protects seating capacity.',
    leaveBlankStricter:
      'Leave blank for no limit. Stricter than the baseline → wins in this window.',
    pacingSimple: 'Pacing (per 15 minutes)',
    concurrentSimple: 'Concurrent (active at once)',
    partySizePacing:
      "Per-party-size pacing buckets are kept as-is - there's no UI for them here yet.",
    deleteShift: 'Delete shift',
    deleteShiftConfirm: (label) => `Delete shift "${label}"?`,
    shiftSaved: 'Shift saved',
    failedSaveShift: 'Failed to save shift',
    failedDeleteShift: 'Failed to delete shift',
    nameRequired: 'Name is required',
    timesMustBeHHmm: 'Times must be HH:mm',
    startBeforeEnd: 'Start must be before end',
    pickAtLeastOneDay: 'Pick at least one day',
    capsPositiveWhole: 'Limits must be positive whole numbers (or blank for no limit)',
    tables: 'Tables',
    tableManagement: 'Table management',
    noZones: 'No zones yet. Add a zone first, then add tables.',
    addAZone: 'Add a zone',
    noTablesYet: 'No tables yet',
    addTablesLink: '+ Add tables',
    seatTables: (seats) => `${seats}-seat tables`,
    seatsTotal: (seats, count) => `${seats * count} seats total`,
    tablesCount: (count) => `${count} tables`,
    addZone: '+ Add zone',
    newZone: 'New zone',
    zoneName: 'Zone name',
    zoneNameExample: 'e.g. Main, Patio, Bar',
    zoneAdded: 'Zone added',
    renameZone: 'Rename zone',
    zoneRenamed: 'Zone renamed',
    deleteZone: 'Delete zone',
    addTableZone: (zone) => `Add tables - ${zone}`,
    seatsPerTable: 'Seats per table',
    numberOfTables: 'Number of tables',
    addedTables: (count) => `${count} tables added`,
    editSeatTables: (seats, zone) => `${seats}-seat tables - ${zone}`,
    setToDeactivateAll: 'Set to 0 to deactivate all',
    tableUpdated: 'Table updated',
    removeAllTables: (count, seats, zone) =>
      `Remove all ${count} × ${seats}-seat tables from ${zone}?`,
    removeTables: 'Remove tables',
    tablePlan: 'Table plan',
    advancedTables: 'Advanced tables',
    simplesMode: 'Simple',
    simpleModeTableDesc: 'Manage tables by count and seat size. No floor plan required.',
    switchSimple: 'Switch to simple mode?',
    advancedModeTable: 'Advanced',
    advancedModeTableDesc:
      'Place tables on a 2D floor plan for precise layout and automatic assignment.',
    switchAdvanced: 'Switch to advanced mode?',
    tableModeColon: (mode) => `Table mode: ${mode}`,
    failedSwitchTableMode: 'Failed to switch table mode',
    tablesFloorPlan: 'Tables & floor plan',
    manageTables: 'Manage tables',
    batchAddTables: 'Batch add tables',
    labelPrefix: 'Label prefix',
    labelPrefixExample:
      'Tables will be labeled T1, T2 … (continuing from the highest existing one)',
    seatCategories: 'Seat categories',
    seats: 'Seats',
    qty: '× Qty',
    addSeatCategory: '+ Add seat category',
    createdTables: (count) => `${count} tables created`,
    editTable: 'Edit table',
    labelOverride: 'Label override',
    useAutoGenerated: 'Leave empty to use the auto-generated label',
    active: 'Active',
    countsForCapacity: 'Counts toward capacity',
    bookableOnline: 'Bookable online',
    tableGone: 'This table no longer exists.',
    decrease: 'Decrease',
    increase: 'Increase',
    noTablesConfigured: 'No tables yet. Use "Batch add" to create tables.',
    floorPlan: 'Floor plan',
    dragDropEditor:
      'The drag-and-drop floor plan editor is coming next. The backend is ready - this screen will render a canvas where you can place tables, resize and rotate them, draw walls, and set a background image.',
    floorPlanVersion: (version, placed, total) =>
      `Version ${version} · ${placed} of ${total} tables placed.`,
    editorLargerScreen:
      "The floor plan editor needs a larger screen with a mouse - open the dashboard on a desktop to edit. Here's a read-only summary:",
    placed: (count) => `Placed (${count})`,
    noTablesPlaced: 'No tables have been placed on the canvas yet.',
    notPlaced: (count) => `Not placed (${count})`,
    zones: 'Zones',
    noZonesYet:
      'No zones yet. Zones group tables that are physically near each other - they limit which tables can be merged for a reservation.',
    deleteZoneConfirm: (label) => `Delete zone "${label}"? Tables are kept but become zone-free.`,
    failedCreateZone: 'Failed to create zone',
    failedDeleteZone: 'Failed to delete zone',
    zoneNamePrompt: 'Zone name (e.g. "Patio", "Indoor")',
    largePartyMessagePrompt:
      'Shown to guests whose party is above the maximum (leave empty to hide)',
    tablesToPlace: (count, plural) => `${count} table${plural} to place`,
    autoPlace: 'Auto place',
    removeTable: 'Remove table',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    fitToTables: 'Fit to tables',
    fitToPlan: 'Fit to plan',
    seater: (seats) => `${seats}-seat`,
    seatersCount: (count) => `· ${count}`,
    // --- AdvancedTablesPage (page-specific copy) ---
    noZoneLabel: 'No zone',
    inactiveBadge: 'Inactive',
    noCapacityBadge: 'No capacity',
    notOnlineBadge: 'Not online',
    seatsCount: (n) => `${n} seats`,
    deleteTablesTitle: 'Delete Tables',
    deleteTablesConfirm: (count, plural) =>
      `Delete ${count} table${plural}? This cannot be undone.`,
    tablesDeleted: (count) => `Deleted ${count} tables`,
    deleteCount: (count) => `Delete (${count})`,
    addCount: (count) => `Add (${count})`,
    floorPlanSaved: 'Floor plan saved',
    failedSaveFloorPlan: 'Failed to save',
    failedBatchCreate: 'Failed',
    done: 'Done',
    aboutMode: (mode) => `About ${mode} mode`,
    noPacingLimit: 'No pacing limit - arrivals are not rate-limited.',
    coversPaceTrips: (max, parties, covers) =>
      `Covers pacing (${max}) trips first: ${parties} parties = ${covers} guests start within 15 minutes, the next is pushed later.`,
    partyPaceTrips: (max, covers) =>
      `Party pacing (${max}) trips first: only ${max} bookings can start per 15 minutes (${covers} guests so far).`,
    bothPacingHit: (parties, covers) =>
      `Both pacing limits reached - ${parties} parties / ${covers} guests within 15 minutes.`,
    noConcurrentLimit: 'No concurrency limit - limited only by your tables.',
    concurrentCoversTrips: (max, covers) =>
      `Concurrent covers (${max}) trips first: ${covers} guests are active at peak.`,
    concurrentPartiesTrips: (max, covers) =>
      `Concurrent parties (${max}) trips first: ${max} bookings active at once (${covers} guests).`,
    bothConcurrentHit: (parties, covers) =>
      `Both concurrency limits reached - ${parties} parties / ${covers} guests at once.`,
    slotFitsTypical: 'A typical mix fits - the slot stays open for more bookings.',
    coversCapsFirst: (max, parties, covers, size) =>
      `Covers cap (${max}) is reached first: ${parties} parties = ${covers} guests, and the next party of ${size} would exceed it.`,
    reservationsCapsFirst: (max, parties, covers) =>
      `Reservations cap (${max}) is reached first: the slot is full at ${parties} bookings, regardless of party size (${covers} guests so far).`,
    bothCapsHit: (parties, covers) =>
      `Both caps are reached at the same time - ${parties} parties / ${covers} guests.`,
    slotClosesWhenHit: 'A slot closes as soon as either limit is reached.',
    noLimitsSlot: 'No limits - the slot never fills on its own (other shifts still apply).',
    onlyOneSlot: 'One 60-minute slot · arriving parties',
    typicalOverlapFits: 'A typical overlap fits - no soft limit reached below your tables.',
    turnedAway: (size) => `${size} turned away`,
    weeklyCompileCoverage: 'Weekly coverage',
    noLimitation: 'no limit',
    resSlot: (count) => `${count} reservations/slot`,
    coversSlot: (count) => `${count} covers/slot`,
    pacingCovers: (count) => `${count} pacing covers`,
    advCapCompact: (pacing, concurrent) => `${pacing} · ${concurrent}`,
    advCap: (pacing, concurrent) => `${pacing}/15' · ${concurrent} concurrent`,
    // --- ReservationSetupPage (page-specific copy) ---
    bookingForm: 'Booking form',
    bookingFormFooter:
      'Optional fields guests can fill when booking - occasion and seating preference.',
    enableBookings: 'Enable bookings',
    enableBookingsAria: 'About enabling bookings',
    enableBookingsTooltip:
      'When off, the booking page is hidden from guests and new online reservations are refused.',
    autoConfirmBookings: 'Auto-confirm bookings',
    autoConfirmAria: 'About confirmation',
    autoConfirmTooltip:
      'On confirms a request instantly. Off holds it as pending until staff review and accept.',
    bookingsAutoConfirmedToast: 'Bookings auto-confirmed',
    bookingsNeedReview: 'Bookings need manual review',
    pauseToday: 'Pause new bookings today',
    pauseTodayAria: 'About pause new bookings',
    pauseTodayTooltip:
      'Stops new online bookings for the rest of today only - future dates stay open.',
    bookingsPausedToday: 'New bookings paused for today',
    bookingsResumed: 'Bookings resumed',
    occasionField: 'Occasion field',
    occasionFieldAria: 'About occasion field',
    occasionFieldTooltip: 'Show or hide the “Occasion” picker on the booking form.',
    occasionFieldShown: 'Occasion field shown',
    occasionFieldHidden: 'Occasion field hidden',
    seatingAccessField: 'Seating & access field',
    seatingAccessAria: 'About seating & access field',
    seatingAccessTooltip:
      'Show or hide the “Seating & access” picker (seating + accessibility, e.g. baby chair) on the booking form.',
    seatingAccessShown: 'Seating & access field shown',
    seatingAccessHidden: 'Seating & access field hidden',
    largestPartyAria: 'About largest party',
    largestPartyTooltip: 'Maximum number of guests allowed per online booking.',
    largePartyMessageAria: 'About large-party message',
    largePartyMessageTooltip:
      'Shown instead of the booking form to groups above the largest party size.',
    bookingHorizonAria: 'About booking horizon',
    bookingHorizonTooltip: 'How many days ahead the booking calendar opens.',
    lastMinuteCutoffMinutes: 'Last-minute cutoff (minutes)',
    lastMinuteCutoffAria: 'About last-minute cutoff',
    lastMinuteCutoffMsg: 'Minutes before the slot to stop taking bookings (0 = up to start)',
    lastMinuteCutoffTooltip: 'Minutes before a slot to stop taking bookings (0 = up to start).',
    minBefore: (minutes) => `${minutes} min before`,
    slotSpacingAria: 'About slot spacing',
    slotSpacingTooltip: 'Minutes between the bookable times guests can choose.',
    lastBookingBeforeCloseAria: 'About last booking before closing',
    lastBookingBeforeCloseTooltip:
      'How long before each service closes to stop offering booking times. Staff can still work past closing, so this is only how late guests may book online — e.g. 60 min means the last slot for a 22:00 close is 21:00.',
    everyMinShort: (minutes) => `Every ${minutes} min`,
    defaultTableTimeMsg: 'How long a table is held per booking, in minutes (min 15)',
    defaultTableTimeAria: 'About default table time',
    defaultTableTimeTooltip:
      'How long a table is held per booking by default. A per-shift override lives in Capacity.',
    shareBookingLink: 'Share booking link',
    shareBookingLinkAria: 'About share booking link',
    shareBookingLinkTooltip: 'Copy your public booking link to share with guests.',
    copy: 'Copy',
    noShowGracePeriodMsg: 'Minutes past the booking time before a guest counts as a no-show',
    noShowGracePeriodAria: 'About no-show grace period',
    noShowGracePeriodTooltip: 'Minutes a booking can be overdue before staff get a no-show alert.',
    minCount: (minutes) => `${minutes} min`,
    fmtNone: 'None',
    fmtHoursMinutes: (hours, minutes) => `${hours}h ${minutes}m`,
    fmtHours: (hours) => `${hours}h`,
    fmtMinutes: (minutes) => `${minutes} min`,
    bookingLinkCopied: 'Booking link copied',
    bookingLink: 'Booking link',
    // --- Zones/Tables/Floor-plan pages (simple mode) ---
    add: 'Add',
    failed: 'Failed',
    rename: 'Rename',
    zonesFooter: 'Tables reference a zone by its id - deleting a zone unzones those tables.',
    deleteZoneNoZoneConfirm: (label) =>
      `Delete "${label}"? Its tables aren't deleted - they move to "No zone" and stay bookable.`,
    orphanTablesNote:
      "These tables belonged to a zone that was deleted. They're still active and bookable. Assign them to a zone by editing each table.",
    floorPlanAriaLabel: 'Restaurant floor plan',
  },

  reservationsFeature: {
    edit: {
      title: 'Edit reservation',
      updated: 'Reservation updated',
      updateFailed: 'Could not update reservation.',
      guestNamePlaceholder: 'Guest name',
      durationPlaceholder: '60',
      notesPlaceholder: 'Staff notes…',
    },
    actions: {
      confirmReservation: 'Confirm reservation',
      declineReservation: 'Decline reservation',
      cancelReservation: 'Cancel reservation',
      markArrived: 'Mark as arrived',
      markNoShow: 'Mark as no-show',
      markCompleted: 'Mark as completed',
    },
    card: {
      guestLabel: (_count) => 'Guest',
      guestLabelPlural: 'Guests',
      swipeAccept: 'Accept',
      swipeDecline: 'Decline',
    },
    inspector: {
      closeButton: 'Close',
      noReservationTitle: 'No reservation selected',
      noReservationDesc: 'Select a reservation from the list to view its details here.',
      notFoundTitle: 'Reservation not found',
      durationUnit: 'min',
      callGuest: 'Call',
      messageGuest: 'Message',
      emailGuest: 'Email',
      editGuest: 'Edit',
      statusLabel: 'Status',
      specialRequests: 'Special requests',
      occasion: 'Occasion',
      seating: 'Seating',
      notes: 'Notes',
      guestInfo: 'Guest info',
      phone: 'Phone',
      source: 'Source',
      internalNote: 'Internal note',
      cancellationReason: 'Cancellation reason',
      timeline: 'Timeline',
      created: 'Created',
      confirmed: 'Confirmed',
      arrived: 'Arrived',
      completed: 'Completed',
      cancelled: 'Cancelled',
      noShow: 'No-show',
      markedArrived: 'Marked as arrived',
      markedCompleted: 'Marked as completed',
      markedNoShow: 'Marked as no-show',
    },
    summary: {
      total: 'Total',
      pending: 'Pending',
      confirmed: 'Confirmed',
      arrived: 'Arrived',
      completed: 'Completed',
      noShow: 'No-show',
    },
    create: {
      title: 'New reservation',
      guestSection: 'Guest',
      nameLabel: 'Full name',
      namePlaceholder: 'e.g. John Smith',
      phoneLabel: 'Phone',
      phonePlaceholder: '+1 555 123 4567',
      emailLabel: 'Email',
      emailPlaceholder: 'john@example.com',
      existingGuests: 'Existing guests - tap to reuse',
      bookingSection: 'Booking',
      dateLabel: 'Date',
      timeLabel: 'Time',
      partySizeLabel: 'Party size',
      sourceSection: 'Source',
      sourcePhone: 'Phone',
      sourceWalkIn: 'Walk-in',
      notesSection: 'Notes',
      notesLabel: 'Internal note',
      notesPlaceholder: 'Staff only...',
      button: 'Create reservation',
      success: 'Reservation created',
      nameRequired: 'Guest name is required.',
      emailRequired: 'Guest email is required.',
      phoneRequired: 'Guest phone is required.',
      noRestaurant: 'No restaurant selected.',
      error: 'Could not create reservation. Please try again.',
    },
    pending: {
      title: 'Pending',
      confirmed: 'Reservation confirmed',
      confirmFailed: 'Could not confirm reservation.',
      rejectPrompt: 'Reason for declining:',
      rejectTitle: 'Decline reservation',
      rejected: 'Reservation declined',
      rejectFailed: 'Could not decline reservation.',
      empty: 'All done!',
      emptyDesc: 'No pending reservations to review.',
    },
    detail: {
      title: 'Reservation',
    },
    list: {
      emptyError: 'Failed to load reservations',
      emptyRetry: 'Please try again.',
      noReservations: 'No reservations',
      noReservationsDesc: 'No reservations for this date.',
      today: 'Today',
      countLabel: (_count) => 'reservation',
      countLabelPlural: 'reservations',
      cancelConfirm: 'Cancel this reservation?',
      cancelTitle: 'Cancel reservation',
      cancelled: 'Reservation cancelled',
      cancelFailed: 'Could not cancel reservation.',
    },
    plan: {
      noZone: 'No zone',
      seatSingular: 'seat',
      seatPlural: 'seats',
      tableAriaLabel: (name, seats) => `Table ${name}, ${seats} ${seats === 1 ? 'seat' : 'seats'}`,
      empty: 'No tables yet',
      emptyDesc: 'Add tables in Settings → Reservation Setup to view the floor plan.',
    },
    timeline: {
      unassignedCount: (count) => `${count} booking${count === 1 ? '' : 's'}`,
      empty: 'Nothing to show yet',
      emptyDesc: 'Add tables in capacity setup and bookings will appear here as bars.',
      seatTopLabel: (seats) => `${seats}-seat table`,
    },
    calendar: {
      error: 'Failed to load reservations',
      errorRetry: 'Please try again.',
    },
  },

  restaurantSetupFeature: {
    branding: {
      title: 'Storefront Appearance',
      storefrontTheme: 'Storefront theme',
      storefrontThemeDescription:
        'Choose the theme of your public storefront. Each preview uses the exact colors and shapes your customers will see.',
      orderButtonPreview: 'Order',
      themeSavedMessage: 'Theme saved - your storefront updates on the next load',
      couldNotSaveTheme: 'Could not save theme.',
      saveTheme: 'Save theme',
      brandingImages: 'Brand images',
      brandingImagesDescription:
        'Logo and cover photo for your public storefront. The cover also appears when your link is shared in chat apps.',
      logoLabel: 'Logo',
      logoHint: 'Square image, shown next to your restaurant name. Drop a file here to upload.',
      coverLabel: 'Cover photo',
      coverHint:
        'Wide image, 1200×630 recommended - shown at the top of your page and in link previews. Drop a file here to upload.',
      uploadImage: 'Upload',
      replaceImage: 'Replace',
      removeImage: 'Remove',
      imageSavedMessage: 'Image saved - your storefront updates on the next load',
      imageRemovedMessage: 'Image removed',
      imageUploadFailed: 'Could not upload image.',
      imageTooLarge: 'Image is too large (max 10 MB).',
      imageWrongType: 'Unsupported file type. Use JPG, PNG or WebP.',
      galleryLabel: 'Photo gallery',
      galleryHint:
        'Up to 9 photos, shown as a grid on your storefront page. Square (1:1) works best. Drag to reorder.',
      addPhotos: 'Add photos',
      dropFilesHint: 'or drop files here',
      galleryFull: 'The gallery holds at most 9 photos.',
    },
  },

  profile: {
    title: 'My Profile',
    personalInfo: 'Personal information',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    notAvailableYet: 'Not available yet',
    contactAdminNote:
      'To update your name or personal details, please contact your administrator - editing isn’t available here yet.',
    currentRestaurantRole: 'Current restaurant role',
    nameInRestaurant: 'Name in restaurant',
    restaurant: 'Restaurant',
    role: 'Role',
  },

  settingsHealth: {
    title: 'Finish setting up',
    location: {
      title: 'Set your restaurant location',
      description: "Delivery can't measure distance until you drop the map pin.",
    },
    openingHours: {
      title: 'Add your opening hours',
      description: 'Your storefront reads as closed until you set them.',
    },
    deliveryPricing: {
      title: 'Set up delivery pricing',
      description: 'Add a delivery zone, or every address is rejected as out of area.',
    },
    deliveryTime: {
      title: 'Set delivery times',
      description:
        'A delivery area with no time gives guests an arrival time that ignores the drive.',
    },
    paymentMethod: {
      title: 'Turn on a payment method',
      description: 'Guests can fill a basket but can’t place the order.',
    },
    menu: {
      title: 'Add items to your menu',
      description: 'Online ordering has nothing to show until your menu has products.',
    },
    menuNotLive: {
      title: 'Switch on a menu for online orders',
      description:
        'No menu is live for pickup or delivery, so guests see "not taking orders online".',
    },
  },

  settingsFeature: {
    features: 'Features',
    privacy: 'Privacy',
    shareUsageData: 'Share usage data',
    shareUsageDataHint:
      'Helps us see which tools you actually use. Never includes guest or order details.',
    assistant: 'Assistant',
    aiAssistant: 'AI Assistant',
    notifications: 'Notifications',
    inbox: 'Inbox',
    inboxNewCount: (count) => `${count} new`,
    reports: 'Reports',
    salesOverview: 'Sales overview',
    dayEnd: 'Day-end',
    topItems: 'Top items',
    paymentReconciliation: 'Payment reconciliation',
    reservationSetup: 'Reservation Setup',
    onlineOrders: 'Online orders',
    orderSetup: 'Order Setup',
    orderShifts: 'Order capacity',
    labels: 'Labels',
    tidyUp: 'Tidy up my lists',
    promotions: 'Promotions',
    loyalty: 'Loyalty',
    giftCards: 'Gift cards',
    staff: 'Staff',
    tableOrdering: 'Table ordering',
    dineInSetup: 'Dine-in setup',
    printing: 'Printing',
    printers: 'Printers',
    customers: 'Customers',
    allCustomers: 'All customers',
    branding: 'Storefront Appearance',
    storefrontLink: 'Storefront link',
    openingHours: 'Opening hours',
    holidays: 'Holidays & Closures',
    reservationHours: 'Reservation Hours',
    orderHours: 'Order Hours',
    pickupHours: 'Pickup Hours',
    deliveryHours: 'Delivery Hours',
    orderHistory: 'Order History',
    payments: 'Payments',
    myProfile: 'My Profile',
  },

  storefrontLink: {
    title: 'Storefront link',
    language: 'Language',
    default: 'Default',
    linkLabel: 'Link',
    linksTitle: 'Links',
    homepage: 'Homepage',
    reservation: 'Reservation',
    onlineOrder: 'Online order',
    linkUnavailable: 'Link unavailable',
    copy: 'Copy',
    copied: 'Copied',
    open: 'Open',
    linkCopied: 'Storefront link copied',
    okButton: 'OK',
  },

  // Who changed which setting, when.
  settingHistory: {
    title: 'Settings history',
    navTitle: 'Settings log',
    empty: 'No settings changes recorded yet.',
    columnActor: 'Changed by',
    columnWhen: 'When',
    columnWhat: 'What changed',
    columnAction: 'Action',
    details: 'Details',
    detailsTitle: 'Change details',
    totalEntries: (total: number) => `${total} entries`,
    actions: {
      CREATED: 'Added',
      UPDATED: 'Edited',
      DELETED: 'Deleted',
    },
    valueHidden: 'changed (value not recorded)',
    on: 'On',
    off: 'Off',
    unitMinutes: (n: string) => `${n} minutes`,
    unitHours: (n: string) => `${n} hours`,
    unitDays: (n: string) => `${n} days`,
    unitPeople: (n: string) => `${n} people`,
    unitKm: (n: string) => `${n} km`,
    photoCount: (n: number) => (n === 1 ? '1 photo' : `${n} photos`),
    partySizePacingRule: (minPartySize: number, maxParties: number) =>
      `${minPartySize}+ guests: max ${maxParties} parties`,
    ruleFields: {
      slotMinutes: 'Slot length',
      maxReservationsPerSlot: 'Max reservations / slot',
      maxCoversPerSlot: 'Max covers / slot',
      pacingMaxCovers: 'Pacing, max covers',
      pacingMaxParties: 'Pacing, max parties',
      maxConcurrentCovers: 'Concurrent, max covers',
      maxConcurrentParties: 'Concurrent, max parties',
      partySizePacing: 'Pacing by party size',
      maxOrdersPerSlot: 'Orders per slot',
      capacityWindowMinutes: 'Capacity window',
    },
    detailBefore: 'Before',
    detailAfter: 'After',
    showRaw: 'Show raw data',
    hideRaw: 'Hide raw data',
    tableAttributes: {
      WINDOW: 'Window',
      BOOTH: 'Booth',
      OUTDOOR: 'Outdoor',
      BAR: 'Bar',
      HIGH_TOP: 'High-top',
      PRIVATE_ROOM: 'Private room',
      QUIET: 'Quiet corner',
      WHEELCHAIR: 'Wheelchair accessible',
      NEAR_FIREPLACE: 'Near the fireplace',
      KIDS_FRIENDLY: 'Kid-friendly',
    },
    fields: {
      name: 'Name',
      address: 'Address',
      city: 'City',
      postalCode: 'Postcode',
      phone: 'Phone',
      email: 'Email',
      timezone: 'Time zone',
      latitude: 'Latitude',
      longitude: 'Longitude',
      isActive: 'Active',
      slug: 'Web address',
      websiteUrl: 'Website',
      facebookUrl: 'Facebook',
      instagramUrl: 'Instagram',
      tiktokUrl: 'TikTok',
      logoKey: 'Logo',
      coverImageKey: 'Cover image',
      galleryKeys: 'Photo gallery',
      themeId: 'Theme',
      shifts: 'Opening times',
      dayOfWeek: 'Day of week',
      startDate: 'Start date',
      endDate: 'End date',
      type: 'Type',
      services: 'Applies to',
      reason: 'Reason',
      label: 'Label',
      sortOrder: 'Display order',
      zoneId: 'Zone',
      seats: 'Seats',
      attributes: 'Attributes',
      customTags: 'Tags',
      countsForCapacity: 'Counts towards capacity',
      isBookableOnline: 'Bookable online',
      isMergeable: 'Can be merged',
      tableMode: 'Table management',
      enabled: 'Online reservations',
      occasionEnabled: 'Ask for occasion',
      seatingPreferenceEnabled: 'Ask for seating preference',
      duration: 'Dining duration',
      bufferTime: 'Buffer between bookings',
      autoConfirmMode: 'Confirmation',
      maxPartySize: 'Maximum party size',
      largePartyMsg: 'Large party message',
      advanceWindow: 'How far ahead guests can book',
      cutoffTime: 'Last-minute cutoff',
      slotInterval: 'Time slot interval',
      closingBufferMinutes: 'Last booking before closing',
      gracePeriod: 'No-show grace period',
      pausedUntil: 'Bookings paused until',
      shiftMode: 'Capacity management',
      scope: 'Applies to',
      startTime: 'Start time',
      endTime: 'End time',
      daysOfWeek: 'Days',
      defaultDurationMinutes: 'Default duration',
      mode: 'Mode',
      rules: 'Capacity rules',
      minOrderCents: 'Minimum order',
      cashEnabled: 'Cash payment',
      onlineEnabled: 'Online payment',
      cardOnHandoffEnabled: 'Card on handover',
      autoConfirmHandoff: 'Auto-accept these orders',
      deliveryPriceMode: 'Delivery pricing',
      distanceCalculation: 'Distance measurement',
      deliveryBufferMinutes: 'Delivery buffer',
      autoCompleteEnabled: 'Automatic completion',
      completeStaleAfterMinutesFromReadyAt: 'Auto-complete orders after',
      cancelPendingAfterHours: 'Cancel unaccepted orders after',
      expireUnpaidAfterMinutes: 'Cancel unpaid orders after',
      prepTimeMinutes: 'Preparation time',
      schedulingLeadMinutes: 'Lead time for scheduled orders',
      openingBufferMinutes: 'Earliest slot after opening',
      credentials: 'API credentials',
      instructions: 'Payment instructions',
      defaultLanguage: 'Venue language',
      feeCents: 'Delivery fee',
      maxDistanceKm: 'Maximum distance',
      deliveryMinutes: 'Delivery time',
      polygon: 'Delivery area',
      displayOrder: 'Display order',
      accountFields: 'Account details',
      qrImageBlobKey: 'QR image',
      color: 'Colour',
      connection: 'Connection',
      isDefault: 'Default printer',
      printOnlineOrders: 'Print online orders',
      printReservations: 'Print reservations',
      textSize: 'Text size',
      splitKitchenTickets: 'Split kitchen tickets',
      printExpoTicket: 'Print expo ticket',
      printItemCategory: 'Show category on ticket',
      dateTimeFormat: 'Ticket date format',
      ownerNotifyEnabled: 'Order alerts',
      ownerNotifyCashEnabled: 'Alerts for cash orders',
      ownerNotifyManualEnabled: 'Alerts for bank transfer orders',
      ownerNotifyOnlineEnabled: 'Alerts for online payments',
      ownerNotifyCancellationEnabled: 'Cancellation alerts',
      ownerNotifyNoShowEnabled: 'No-show alerts',
      prepReminderEnabled: 'Start-prep reminder',
      customerEmailEnabled: 'Guest emails',
      customerEmailCashEnabled: 'Guest emails for cash orders',
      customerEmailManualEnabled: 'Guest emails for bank transfers',
      customerEmailOnlineEnabled: 'Guest emails for online payments',
      ownerEmailEnabled: 'Owner emails',
      reminderEnabled: 'Reminder emails',
      reminderOffsetsMinutes: 'Reminder timing',
    } as Record<string, string>,
    values: {
      STRAIGHT_LINE: 'Distance tier',
      POLYGON: 'Drawn area',
      ROUTE_DISTANCE: 'Driving distance',
      AUTO: 'Automatic',
      MANUAL: 'Manual',
      SIMPLE: 'Simple',
      ADVANCED: 'Advanced',
      BASELINE: 'Default hours',
      WINDOW: 'Time window',
      CLOSED: 'Closed',
      MODIFIED: 'Modified hours',
      DISTANCE: 'By distance',
      ZONE: 'By zone',
      DIRECT: 'Straight line',
      ROUTE: 'Road distance',
      MEDIUM: 'Medium',
      LARGE: 'Large',
      DMY_SLASH: 'DD/MM/YYYY',
      DMY_DOT: 'DD.MM.YYYY',
      YMD_DASH: 'YYYY-MM-DD',
      MDY_SLASH: 'MM/DD/YYYY',
      PICKUP: 'Pickup',
      DELIVERY: 'Delivery',
      DINE_IN: 'Dine-in',
    } as Record<string, string>,
  },

  staff: {
    title: 'Staff',
    inviteTitle: 'Invite team member',
    nameLabel: 'Name',
    namePlaceholder: 'Jane Smith',
    emailLabel: 'Email',
    emailPlaceholder: 'jane@example.com',
    roleLabel: 'Role',
    roles: {
      MANAGER: 'Manager',
      STAFF: 'Server',
      KITCHEN: 'Kitchen',
      CASHIER: 'Cashier',
    },
    inviting: 'Inviting…',
    sendInvite: 'Send invite',
    couldNotLoadTitle: 'Could not load staff',
    noStaffTitle: 'No staff yet',
    noStaffDescription:
      'Invite your team - servers, kitchen, cashier - and set what each can do. Tap +.',
    inactive: 'Inactive',
    roleAriaLabel: (name) => `Role for ${name}`,
    remove: 'Remove',
    validation: {
      nameAndEmail: 'Enter a name and an email address.',
    },
    toast: {
      invitationSent: 'Invitation sent',
      couldNotInvite: 'This person could not be invited.',
      couldNotChangeRole: 'Could not change role.',
      couldNotUpdate: 'Could not update.',
      removed: (name) => `${name} removed`,
      couldNotRemove: 'Could not remove.',
    },
    removeConfirm: (name) => `Remove ${name} from this restaurant?`,
    removeConfirmTitle: 'Remove staff member',
  },

  tableOrdering: {
    attachReservation: {
      title: 'Attach reservation',
      noAttachable: 'No reservations available to attach.',
      confirmAttach: (name: string) => `Attach ${name}'s reservation and mark them arrived?`,
      partyOf: (n: number) => `Party of ${n}`,
      onThisTable: ' · this table',
    },
    inspector: {
      selectTable: 'Select a table',
      selectTableDesc:
        'Select a table on the floor plan to see the bill, take orders and collect payment.',
      addRound: 'Add round',
      bill: 'Bill',
      attach: 'Attach',
      guest: 'Guest',
      addGuest: 'Add guest',
      activity: 'Activity',
      needsAttention: 'Needs attention',
      guestCalledServer: 'Guest called the server',
      guestRequestedBill: 'Guest requested the bill',
      resolve: 'Resolve',
      rounds: 'Rounds',
      noRounds: 'No rounds yet - "Add round" to take the order.',
      total: 'Total',
      paid: 'Paid',
      billRequestedStatus: 'Bill requested',
      openStatus: 'Open',
      closedStatus: 'Closed',
      walkIn: 'Walk-in',
      openedTime: 'opened',
      visit: 'visit',
      visits: 'visits',
      visitsCount: (n: number) => `${n} ${n === 1 ? 'visit' : 'visits'}`,
      roundStatus: {
        placed: 'Awaiting confirmation',
        fired: 'In the kitchen',
        ready: 'Ready',
        served: 'Served',
        cancelled: 'Cancelled',
      },
      roundNum: (seq: number) => `Round ${seq}`,
      roundFired: (seq: number) => `Round ${seq} fired`,
      roundAccepted: (seq: number) => `Round ${seq} accepted`,
      roundReadyToast: (seq: number) => `Round ${seq} ready`,
      roundServedToast: (seq: number) => `Round ${seq} served`,
      roundReopened: (seq: number) => `Round ${seq} back in the kitchen`,
      cancelRoundPrompt: 'Reason for cancelling this round',
      roundCancelledToast: (seq: number) => `Round ${seq} cancelled`,
      voidItemPrompt: (name: string) => `Void "${name}"? Add a reason (optional)`,
      voidItemTitle: 'Void item',
      restoreAria: (name: string) => `Restore ${name}`,
      voidAria: (name: string) => `Void ${name}`,
    },
    roundActions: {
      acceptFire: 'Accept & fire',
      cancelRound: 'Cancel round',
      markReady: 'Mark as ready',
      markServed: 'Mark as served',
      sendBackKitchen: 'Send back to kitchen',
    },
    zonePicker: {
      open: 'open',
      noOpenTabs: 'No open bills',
      openCount: (n: number) => `${n} open`,
    },
    assignCustomer: {
      title: 'Assign guest',
      change: 'Change guest',
      currentGuest: 'Current guest',
      unlinkGuest: 'Unlink guest',
      findAddGuest: 'Find or add a guest',
      linkDifferent: 'Link a different guest',
      nameLabel: 'Name',
      guestNamePlaceholder: 'Guest name',
      phoneLabel: 'Phone',
      phoneNumberPlaceholder: 'Phone number',
      existingGuests: 'Existing guests - tap to link',
      linkNewGuest: 'Link new guest',
      linkGuest: 'Link guest',
      guestLinked: 'Guest linked',
      guestUnlinked: 'Guest unlinked',
      enterNamePhone: 'Enter a name and a phone number.',
      linkedName: (name: string) => `Linked ${name}`,
      visitsFooter: (n: number) => `${n} visit${n === 1 ? '' : 's'}`,
    },
    floorPlan: {
      noFloorPlanAdvanced:
        'No floor plan drawn yet - tables are shown grouped by zone. Draw a floor plan under Settings → Tables (Advanced) and it will appear here.',
      simpleMode:
        'Tables are in simple mode - shown grouped by zone. Switch to "Advanced" under Settings → Floor plan to create a floor plan.',
    },
    tableGrid: {
      couldntLoadTables: 'Could not load tables',
      noTables: 'No tables',
      noTablesDesc: 'Add tables under Settings → Tables to start taking orders.',
      noTablesZone: 'No tables in this zone',
      noTablesZoneDesc: 'Pick another zone or add tables under Settings → Tables.',
      other: 'Other',
      reserved: 'Reserved',
      free: 'Free',
      openTab: 'Open tab',
      tryAgain: 'Please try again.',
      zoneCount: (label: string, n: number) => `${label} · ${n}`,
      occupiedSub: (rounds: number, money: string, calls: number) =>
        `${rounds} round${rounds === 1 ? '' : 's'} · ${money}${calls > 0 ? ` · ${calls} call` : ''}`,
      reservedAt: (time: string) => `Reserved · ${time}`,
      seatsCount: (n: number) => `${n} seats`,
      roundsAbbrev: (n: number) => `${n}r`,
    },
    billCollect: {
      collectPayments: 'Collect payments',
      noSplit: 'No split yet',
      noSplitDesc: 'Set up a split first - then each share appears here to collect.',
      splitBill: 'Split bill',
      collectedFull: 'Fully collected',
      shares: 'Shares',
      awaiting: 'Awaiting · tap to collect',
      done: 'Done',
      tapNote:
        'Tap each pending share to collect the payment. The bill closes itself when the last share is collected.',
      collectedCountOf: (count: number, total: number) => `${count} of ${total} collected`,
      shareNum: (i: number) => `Share ${i}`,
      methodTipFooter: (method: string, tip: string) => `${method} · +${tip} tip`,
      methodCollectedFooter: (method: string) => `${method} · collected`,
    },
    addRound: {
      title: 'Add round',
      send: 'Send',
      menu: 'Menu',
      order: 'Order',
      searchMenu: 'Search the menu',
      noMenu: 'No active menu available. Publish a menu first.',
      chooseOptions: 'Choose options',
      noItems: 'No items yet - switch to the menu and tap dishes to build the round.',
      roundSentKitchen: 'Round sent to kitchen',
      failedSend: 'Failed to send round',
      assignTo: 'Assign this item to',
      shared: 'Shared',
      addSeat: '+ Add seat',
      seatAdded: 'Seat added',
      edit: 'Edit',
      noteName: 'Note for the kitchen (optional)',
      orderTab: (count: number) => `Order${count > 0 ? ` · ${count}` : ''}`,
      seatOrdinal: (n: number) => `Seat ${n}`,
      addedItem: (name: string) => `Added ${name}`,
      tapDishes: 'Tap dishes to build the round.',
      summaryLine: (count: number, total: string) =>
        `${count} item${count === 1 ? '' : 's'} · ${total} - "Send" to fire the round`,
    },
    configureItem: {
      title: 'Configure',
      dishUnavailable: 'Dish unavailable',
      dishUnavailableDesc: 'This dish is no longer on the menu. Go back and pick another.',
      updateItem: (money: string) => `Update · ${money}`,
      addItem: (money: string) => `Add · ${money}`,
      chooseOne: 'Choose 1',
      chooseAny: 'Choose any',
      chooseExact: (n: number, picked: number) => `Choose ${n} · ${picked}/${n}`,
      upTo: (n: number, picked: number) => `Up to ${n} · ${picked}/${n}`,
      chooseRange: (min: number, max: number, picked: number) =>
        `Choose ${min}-${max} · ${picked}/${max}`,
      atLeast: (n: number, picked: number) => `At least ${n} · ${picked}`,
      pickMore: (count: number, name: string) => `Pick ${count} more ${name}`,
      maxReached: (max: number) => `Max ${max} reached - remove one to change`,
      optionShortage: (name: string, available: number, total: number, min: number) =>
        `${name}: only ${available} of ${total} available, need ${min}`,
      temporarilyUnavailable: 'Temporarily unavailable',
      required: 'Required',
      optional: 'Optional',
    },
    settings: {
      title: 'Table ordering',
      dineInOrdering: 'Dine-in ordering',
      enabled: 'Enabled',
      tableOrderingOn: 'Table ordering enabled',
      tableOrderingOff: 'Table ordering disabled',
      friendlyMessage:
        'When disabled, scanning a table QR code shows a friendly "Ask the staff" message and no menu.',
      howItWorks: 'How it works',
      orderingMode: 'Ordering mode',
      modeGuest: 'Guest',
      modeStaff: 'Staff',
      modeHybrid: 'Hybrid',
      roundHandling: 'Round handling',
      autoPrintKitchen: 'Auto-print kitchen tickets',
      charges: 'Charges',
      serviceCharge: 'Service charge',
      tipSuggestions: 'Tip suggestions',
      tableQRCodes: 'Table QR codes',
      printableQR: 'Printable QR code sheet',
      printQRDesc:
        'One QR code per table. Print, cut and place on the tables - guests scan to open their bill.',
      whoPlacesOrders: 'Who places orders?',
      guestOrderThemselves: 'Guests order themselves',
      staffTakeOrders: 'Staff take all orders',
      guestOrderStaffConfirm: 'Guests order, staff confirm first round',
      orderingModeUpdated: 'Ordering mode updated',
      whenRoundPlaced: 'When a round is placed',
      sendKitchenImmediately: 'Send to the kitchen immediately',
      holdStaffAccept: 'Hold until staff accept',
      roundHandlingUpdated: 'Round handling updated',
      serviceChargePrompt: 'Service charge added to every bill (percent, e.g. 12.5). 0 to disable.',
      enter0100: 'Enter 0–100',
      serviceChargeUpdated: 'Service charge updated',
      tipPresetsPrompt:
        'Suggested tip buttons shown to guests - percentages separated by comma (e.g. 5, 10, 15). Empty for none.',
      tipPresetsUpdated: 'Tip suggestions updated',
      firingAuto: 'Auto',
      firingConfirm: 'Confirm',
      kitchenTicketUpdated: 'Kitchen ticket setting updated',
      chargeOff: 'Off',
      tipsNone: 'None',
    },
    billSplit: {
      title: 'Split bill',
      billTotal: 'Bill total',
      even: 'Even',
      byAmount: 'By amount',
      bySeat: 'By seat',
      noSeats:
        'No seats yet. Add seats (or tag dishes to a seat on the order pad), then each seat pays for their own - shared dishes are split evenly.',
      noItems: 'No items yet',
      nothingShared: 'Nothing shared',
      moveItem: 'Move item to',
      seatAdded: 'Seat added',
      atLeastOneSeat: 'Add at least one seat first',
      atleastTwoAmounts: 'Enter at least two amounts',
      splitCreated: 'Split created',
      createSplit: 'Create split',
      seatFallback: (ordinal: number) => `Seat ${ordinal}`,
      movedToSeat: (label: string) => `Moved to ${label}`,
      splitNote: (n: number) =>
        `Tap an item to move it. Shared dishes split evenly across ${n} seat${n === 1 ? '' : 's'}.`,
      perPersonWays: (ways: number) => `per person · ${ways} ways`,
      partyLabel: (n: number) => `Party ${n}`,
      addParty: 'Add a party',
    },
    history: {
      title: 'History',
      couldntLoadHistory: 'Could not load history',
      noClosedTabs: 'No closed bills',
      noClosedTabsDesc: 'Bills settled on this day appear here. Pick another day above.',
      splitEven: 'Split · even',
      splitByAmount: 'Split · by amount',
      splitBySeat: 'Split · by seat',
      single: 'Single',
      forceClosed: 'Force closed',
      tryAgain: 'Please try again.',
      tabsSummary: (n: number, money: string) =>
        `${n} tab${n === 1 ? '' : 's'} · ${money} collected`,
      closedAt: (time: string) => `Closed ${time}`,
      roundsCount: (n: number) => `${n} round${n === 1 ? '' : 's'}`,
      mixed: 'Mixed',
      card: 'Card',
      giftCard: 'Gift card',
    },
    bill: {
      title: 'Bill',
      paidInFull: 'Paid in full',
      balanceDue: 'Balance due',
      subtotal: 'Subtotal',
      serviceCharge: 'Service charge',
      finishMeal: 'Finish the meal before billing',
      backToTab: 'Back to bill',
      settle: 'Settle',
      collectPayments: 'Collect payments',
      takePayment: 'Take a payment',
      splitBill: 'Split bill',
      splitOptions: 'Even, by amount or by seat',
      takeFullPayment: 'Take full payment',
      paymentsTaken: 'Payments taken',
      manage: 'Manage bill',
      viewActivity: 'View activity',
      markBillRequested: 'Mark as requested',
      moveTabAnother: 'Move bill to another table',
      mergeAnother: 'Merge another bill',
      forceCloseWalkout: 'Force close (walkout / complimentary)…',
      tabClosed: 'Bill closed',
      tabClosedDesc: 'This bill is settled and closed. The summary above is read-only.',
      closeConfirm: 'Close this fully paid bill?',
      closeTitle: 'Close bill',
      forceClosePrompt: 'Reason (walkout, complimentary, error…) - noted in the timeline',
      forceCloseTitle: 'Force close bill',
      forceCloseDone: 'Bill force closed',
      noFreeTable: 'No free table to move to',
      moveTitle: 'Move bill to',
      noOtherTab: 'No other open bill to merge',
      mergeTitle: 'Merge another bill into this one',
      mergeDesc: "The other tab's rounds are moved here; it closes as merged.",
      markedBillRequested: 'Marked as requested',
      roundsNotServed: (count: number, seqs: string) =>
        `Round${count !== 1 ? 's' : ''} ${seqs} ${count !== 1 ? 'are' : 'is'} not served yet`,
      finishMealDesc: (roundsText: string) =>
        `${roundsText}. Serve or cancel every round on the bill, then come back to settle.`,
      collectPaymentsFooter: (collected: number, total: number, amount: string) =>
        `${collected} of ${total} collected · ${amount} left`,
      takePaymentFooter: (amount: string) => `${amount} outstanding · cash or card`,
      guestSuggested: (mode: string) => `Guest suggested: ${mode}`,
      bySeatFooter: (n: number) => `By seat (${n}), evenly or by amount`,
      takeFullPaymentFooter: (amount: string) => `${amount} · one payment`,
      paymentTipFooter: (status: string, tip: string) => `${status} · +${tip} tip`,
      payStatus: {
        pendingCollection: 'Awaiting payment',
        awaitingProof: 'Awaiting proof',
        proofSubmitted: 'Proof submitted',
        processing: 'Processing',
        paid: 'Paid',
        rejected: 'Rejected',
        void: 'Void',
        refunded: 'Refunded',
        partiallyRefunded: 'Part. refunded',
      },
      movedTo: (label: string) => `Moved to ${label}`,
      mergedWith: (label: string) => `Merged ${label}`,
      billTitleFor: (label: string) => `Bill · ${label}`,
    },
    payment: {
      collect: 'Collect a share',
      takePayment: 'Take a payment',
      method: 'Method',
      cash: 'Cash',
      cardTerminal: 'Card / Terminal',
      amount: 'Amount',
      tip: 'Tip',
      enterAmount: 'Enter a payment amount',
      shareCollected: 'Share collected',
      paymentRecorded: 'Payment recorded',
      shareOf: (i: number, total: number) => `Share ${i} of ${total}`,
      collectAmount: (amount: string) => `Collect ${amount}`,
      recordAmountPayment: (amount: string) => `Record ${amount} payment`,
    },
    workspace: {
      tables: 'Tables',
      floor: 'Floor plan',
      list: 'List',
      openTabTitle: 'Open bill',
      noZone: 'No zone',
      zoneFallback: 'Zone',
      openTabConfirm: (label: string) => `Open a new tab for ${label}?`,
      tabOpened: (label: string) => `Tab opened · ${label}`,
      tabFallback: 'Tab',
    },
    qrSheet: {
      title: 'QR code sheet',
      print: 'Print',
      noQR: 'No table QR codes',
      noQRDesc: 'Add tables under Settings → Tables; each gets a QR code automatically.',
      hint: 'Print this page, cut along the cards and place one on each table.',
      noQRYet: 'No QR code yet:',
      reseeding: 'These tables need a QR token - re-seeding or saving the table assigns one.',
      tryAgain: 'Please try again.',
      qrAlt: (label: string) => `QR for ${label}`,
    },
    timeline: {
      activity: 'Activity',
      noActivity: 'No activity yet',
      noActivityDesc: 'Every action on this bill - orders, kitchen, payments - appears here.',
      tabOpened: 'Bill opened',
      roundPlaced: 'Round placed',
      sentKitchen: 'Sent to the kitchen',
      roundReady: 'Round ready',
      roundServed: 'Round served',
      roundCancelled: 'Round cancelled',
      itemVoided: 'Item voided',
      itemRestored: 'Item restored',
      reservationAttached: 'Reservation attached',
      serviceRequested: 'Service requested',
      serviceResolved: 'Service resolved',
      billRequested: 'Bill requested',
      paymentTaken: 'Payment taken',
      forceClosedTimeline: 'Force closed',
      movedTable: 'Table moved',
      mergedAnother: 'Merged into another bill',
      tabClosed3: 'Bill closed',
      seatedReservation: 'Seated from reservation',
      guest: 'Guest',
      staff: 'Staff',
      system: 'System',
    },
    arrivals: {
      title: 'Expected arrivals',
      noArrivals: 'No expected arrivals',
      noArrivalsDesc: "Today's upcoming bookings appear here to seat with a tap.",
      overdue: 'Overdue',
      seatTitle: 'Seat guest',
      seatConfirm: (name: string, n: number) => `Seat ${name} (party of ${n})?`,
      seatedToast: (name: string) => `Seated ${name}`,
    },
  },

  waitlist: {
    title: 'Waitlist',
    partyOf: (partySize) => `Party of ${partySize}`,
    addForm: {
      title: 'Add walk-in',
      partySize: 'Party size',
      guestName: 'Guest name',
      guestNamePlaceholder: 'e.g. Sarah',
      phone: 'Phone (optional)',
      phonePlaceholder: 'To call them when a table is free',
      adding: 'Adding…',
      addButton: 'Add to waitlist',
    },
    validation: {
      nameRequired: "Please enter the guest's name.",
    },
    toasts: {
      added: (guestName, quotedWaitMinutes) =>
        `${guestName} added - estimated wait ${quotedWaitMinutes} minutes`,
      marked_notified: (guestName) => `${guestName} has been notified`,
      could_not_update: 'Update failed.',
      no_free_tables: 'No free tables right now.',
      seated: (guestName) => `${guestName} seated`,
      could_not_seat: 'This party could not be seated.',
      removed: (guestName) => `${guestName} removed`,
      could_not_remove: 'Removal failed.',
      marked_noshow: (guestName) => `${guestName} marked as no-show`,
      could_not_add: 'Could not add that party.',
    },
    actions: {
      seat: {
        label: (guestName, partySize) => `Seat ${guestName} (${partySize} guests)`,
        tableOption: (tableLabel, zoneLabelFormatted, seats) =>
          `${tableLabel}${zoneLabelFormatted} - ${seats} seats`,
      },
      seatButton: 'Seat',
      cancel: {
        confirm: (guestName) => `Remove ${guestName} from the waitlist?`,
        title: 'Remove party',
      },
      noShow: {
        confirm: (guestName) => `Mark ${guestName} as no-show?`,
        title: 'No-show',
      },
      notify: 'Notify',
      noshow: 'No-show',
      remove: 'Remove',
    },
    error: {
      loading: 'Could not load waitlist',
    },
    empty: {
      title: 'Nobody waiting',
      description:
        'When a party comes in and no table is free, add them here and quote the wait time. Press +.',
    },
    status: {
      notified: 'Notified',
    },
  },

  shared: {
    restaurantNotFound: 'This restaurant no longer exists - pick another one.',
    failedLoadRestaurantConfig: 'Failed to load restaurant configuration.',
    unableToConnect: 'Unable to connect. Please check your connection.',
    retry: 'Retry',
    chooseAnotherRestaurant: 'Choose another restaurant',
    language: 'Language',
    empty: 'empty',
    noRestaurantsAvailable: 'No restaurants available.',
    restaurant: 'Restaurant',
  },

  forms: {
    required: 'This field is required.',
    tooLong: (max) => `Use ${max} characters or fewer.`,
    invalidEmail: 'Enter a valid email like hello@restaurant.com.',
    invalid: 'Check this value.',
    invalidUrl: 'Enter a valid web address.',
  },

  // Order label settings (create / rename / recolor the triage tag set).
  purge: {
    history: {
      pageTitle: 'Hidden history',
      couldntLoad: "Couldn't load the history",
      empty: 'Nothing tidied up yet',
      emptyWhy: 'Once something is hidden — by you or overnight — it shows up here.',
      footer: 'Kept so you can always see what was hidden, and why.',
      hidSummary: (what: string) => `Hid ${what}`,
      orders: (n: number) => `${n.toLocaleString()} ${n === 1 ? 'order' : 'orders'}`,
      reservations: (n: number) =>
        `${n.toLocaleString()} ${n === 1 ? 'reservation' : 'reservations'}`,
      statusLabel: {
        RUNNING: 'Running',
        COMPLETED: 'Done',
        PARTIAL: 'Only part of it',
        FAILED: "Didn't finish",
      },
      sourceLabel: {
        SCHEDULED: 'Overnight',
        MANUAL_RULE: 'By hand',
        MANUAL_CRITERIA: 'By hand',
        MANUAL_SELECTION: 'By hand',
      },
      detailTitle: 'Clean-up details',
      when: 'When',
      outcome: 'Outcome',
      hiddenCount: 'Records hidden',
      startedBy: 'Started',
      criteriaTitle: 'What it looked for',
      whatKind: 'Kind',
      fromRule: 'Rule',
      olderThan: 'Older than',
      tags: 'Tags',
      pickedByHand: 'Picked by hand',
      criteriaFrozen: 'Saved as it was at the time, so renaming a tag later never changes this.',
      skippedTitle: 'Left alone',
      skippedFooter: 'These matched, but were protected. Nothing here was hidden.',
      skipReason: {
        STILL_LIVE: 'Still active',
        CARRIES_MONEY: 'Involves money',
        CONCURRENT_CHANGE: 'Changed while running',
        CAP_REACHED: 'Too many at once',
      },
      errorTitle: 'What went wrong',
    },
    cleanupTitle: 'Clean up now',
    cleanupIntro:
      'Hide a batch of old, finished records in one go. Nothing is erased, but hidden orders stop counting towards your sales reports.',
    cleanupTagsTitle: 'Limit to tags',
    pickATagToCount: 'Choose at least one tag to see how many would be hidden.',
    cleanupSomeTags: 'Only records carrying one of the tags above are included.',
    cleanupReassurance:
      'Only completed and cancelled orders are eligible. An order is included when it has no payment, its payment was rejected or voided, it was completed and paid in cash, or it was cancelled and fully refunded.',
    working: 'Working…',
    confirmTitle: 'Hide these?',
    zeroDayAutoTitle: 'Clean up from today?',
    zeroDayAutoBody:
      'With "Before today" and auto on, every matching item older than midnight is removed on each scheduled run, including today’s. This cannot be undone. Continue?',
    autoTitle: 'Auto hidden',
    autoOnFooter:
      'Runs on the schedule in Advanced settings. You can still tidy up by hand any time.',
    pageTitle: 'Tidy up my lists',
    intro:
      'Old finished records are hidden from your lists, not deleted. Hidden orders stop counting towards your sales reports, and every clean-up you run is listed in Hidden history.',
    couldntLoad: "Couldn't load your tidy-up settings",
    noAccess: "You don't have access to this page",
    couldNotSave: "Couldn't save. Try again.",
    noLabels: 'No tags set up yet.',
    pickATagFirst: 'Choose at least one tag before turning this on.',
    dayRequired: 'Keep at least one day selected.',
    counting: 'Counting…',
    couldntCount: "Couldn't count right now. Check your connection and try again.",
    seeThem: 'See them',
    close: 'Close',
    loadMore: 'Load more',
    nothingToShow: 'Nothing to show.',
    showingOf: (a: number, b: number) => `Showing ${a} of ${b.toLocaleString()}.`,
    olderThanOption: (days: number): string =>
      days === 0
        ? 'Before today'
        : days === 1
          ? '1 day'
          : days === 30
            ? '1 month'
            : days === 90
              ? '3 months'
              : days === 180
                ? '6 months'
                : '1 year',
    advancedTitle: 'Advanced settings',
    advancedFooter: 'Fine-tune how age is measured and when the nightly clean-up runs.',
    advanced: {
      ageBasisLabel: 'Age counted from',
      ageBasisCreated: 'When it was created',
      ageBasisService: 'Its service time',
      matchLabel: 'Must carry',
      matchAny: 'Any of these tags',
      matchAll: 'All of these tags',
      kindLabel: 'Runs',
      kindDaily: 'Every day',
      kindWeekly: 'Every week',
      kindMonthly: 'Every month',
      weekdayLabel: 'On',
      monthDayLabel: 'On day',
      hourLabel: 'At',
      hourOption: (h: number) => `${String(h).padStart(2, '0')}:00`,
      scheduleFooter: "The nightly clean-up runs at this time, in your restaurant's local time.",
    },
    managedInAdvanced: 'Managed by custom rules',
    managedInAdvancedFooter: (n: number) =>
      `This list has ${n} custom ${n === 1 ? 'rule' : 'rules'} that were not created on this screen. The controls are hidden so they cannot silently edit just one of them.`,
    orders: {
      confirmBody: (n: number) =>
        `${n.toLocaleString()} ${n === 1 ? 'order' : 'orders'} will be hidden from this list and will no longer count towards your sales reports.`,
      hideAction: (n: number) => `Hide ${n.toLocaleString()} ${n === 1 ? 'order' : 'orders'}`,
      hidden: (done: number, asked: number) =>
        done === asked
          ? `Hid ${done.toLocaleString()} ${done === 1 ? 'order' : 'orders'}.`
          : `Hid ${done.toLocaleString()} of ${asked.toLocaleString()}. The rest changed while you were choosing.`,
      title: 'Orders',
      previewTitle: 'Orders that would be hidden',
      switchTitle: 'Automatically tidy my order list',
      olderThanLabel: 'Hide completed & cancelled orders older than',
      wouldHide: (n: number) =>
        n === 0
          ? 'Nothing to hide right now.'
          : `Right now this would hide ${n.toLocaleString()} ${n === 1 ? 'order' : 'orders'}.`,
    },
    reservations: {
      confirmBody: (n: number) =>
        `${n.toLocaleString()} ${n === 1 ? 'reservation' : 'reservations'} will be hidden from this list. Your reports do not change.`,
      hideAction: (n: number) =>
        `Hide ${n.toLocaleString()} ${n === 1 ? 'reservation' : 'reservations'}`,
      hidden: (done: number, asked: number) =>
        done === asked
          ? `Hid ${done.toLocaleString()} ${done === 1 ? 'reservation' : 'reservations'}.`
          : `Hid ${done.toLocaleString()} of ${asked.toLocaleString()}. The rest changed while you were choosing.`,
      title: 'Reservations',
      previewTitle: 'Reservations that would be hidden',
      switchTitle: 'Automatically tidy my reservation list',
      olderThanLabel: 'Hide finished reservations older than',
      wouldHide: (n: number) =>
        n === 0
          ? 'Nothing to hide right now.'
          : `Right now this would hide ${n.toLocaleString()} ${n === 1 ? 'reservation' : 'reservations'}.`,
    },
  },
  labels: {
    none: 'No labels yet.',
    setUp: 'Set up labels',
    couldNotAssign: "Couldn't save labels. Try again.",
    more: (n: number) => `+${n}`,
    systemOwnedNote: 'This is a built-in label. You can rename or recolor it, but not delete it.',
    pageTitle: 'Labels',
    addLabel: 'New label',
    editLabel: 'Edit label',
    nameLabel: 'Name',
    namePlaceholder: 'e.g. VIP',
    nameRequired: 'Enter a label name.',
    colorLabel: 'Color',
    customColor: 'Custom color',
    preview: 'Preview',
    labelAdded: 'Label added',
    labelUpdated: 'Label updated',
    labelDeleted: 'Label deleted',
    deleteTitle: 'Delete label',
    deleteConfirmation: (name: string) =>
      `Delete "${name}"? It will be removed from every order and reservation it's on.`,
    couldNotSave: "Couldn't save the label.",
    couldNotDelete: "Couldn't delete the label.",
    couldntLoadLabels: "Couldn't load labels",
    noLabelsTitle: 'No labels yet',
    noLabelsDescription:
      'Create labels like "VIP" or "Complaint", then tap them onto an order or reservation to flag it for the team.',
    listFooter:
      "Staff can tap these onto any order or reservation. Deleting a label removes it from every record it's on.",
    cancel: 'Cancel',
  },
  suspension: {
    heading: 'This restaurant has been suspended',
    body: 'Access to the app is paused for now. Contact support to get it back.',
    reasonLabel: 'Reason',
    contactLabel: 'Support',
    whatsApp: 'Chat on WhatsApp',
    checkAgain: 'Check again',
  },
  voiceInput: {
    start: 'Dictate',
    stop: 'Stop dictation',
    recordingOnly: 'Recording — your words appear when you stop.',
    permissionDenied:
      'Microphone access is off. Allow it in your device or browser settings to dictate.',
    noMicrophone: 'No microphone found on this device.',
    unsupported: 'Dictation is not available on this device.',
    failed: 'Could not turn your voice into text. Please try again.',
  },
};

export type Translations = typeof en;
